import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ScatterChart, Scatter, PieChart, Pie, Cell,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import {
  FiArrowLeft, FiRefreshCw, FiPlus, FiTrendingUp, FiTarget,
  FiCheckCircle, FiActivity, FiSearch, FiChevronDown, FiChevronUp,
  FiX, FiCheck, FiGrid, FiFileText, FiEye, FiDownload, FiSliders,
  FiAlertTriangle,
} from 'react-icons/fi';
import * as api from '../../utils/forecastingApi';
import SFChatWidget from './SFChatWidget';
import '../../Styles/SalesForecasting.css';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (n, currency = 'USD') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(Number(n) || 0);

const CURRENCY_SYM = { USD: '$', INR: '₹', EUR: '€', GBP: '£' };

const fmtM = (n, currency = 'USD') => {
  const num = Number(n) || 0;
  const sym = CURRENCY_SYM[currency] || '';
  if (Math.abs(num) >= 1_000_000) return `${sym}${(num / 1_000_000).toFixed(1)}M`;
  if (Math.abs(num) >= 1_000)     return `${sym}${(num / 1_000).toFixed(0)}K`;
  return fmt(n, currency);
};

const pct = (n) => `${(Number(n) || 0).toFixed(1)}%`;

const fmtPeriod = (start, end) => {
  if (!start || !end) return '—';
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const s = new Date(start), e = new Date(end);
  if (s.getFullYear() === e.getFullYear())
    return `${months[s.getMonth()]} ${s.getDate()} – ${months[e.getMonth()]} ${e.getDate()}, ${s.getFullYear()}`;
  return `${months[s.getMonth()]} ${s.getFullYear()} – ${months[e.getMonth()]} ${e.getFullYear()}`;
};

const fmtQuarter = (start) => {
  if (!start) return '—';
  const d = new Date(start);
  return `Q${Math.ceil((d.getMonth() + 1) / 3)} ${d.getFullYear()}`;
};

const STATUS_CLASS = {
  PENDING: 'amber', RUNNING: 'blue', COMPLETED: 'green', FAILED: 'red',
  DRAFT: 'gray', ACTIVE: 'green', APPROVED: 'green', REJECTED: 'red',
  ESCALATED: 'orange', HIGH: 'red', MEDIUM: 'amber', LOW: 'blue',
  OPEN: 'amber', CLOSED: 'gray', IN_REVIEW: 'blue',
};

const DIM_BADGE_CLASS = {
  REGION: 'sf-dim-region', REP: 'sf-dim-rep',
  PRODUCT: 'sf-dim-product', CUSTOMER: 'sf-dim-customer',
};

function stableHash(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < (str || '').length; i++) {
    h ^= str.charCodeAt(i);
    h = (h + (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24)) | 0;
  }
  return Math.abs(h >>> 0);
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

function useCountUp(target, duration = 950) {
  const [val, setVal] = useState(0);
  const raf = useRef(null);
  const t0  = useRef(null);
  useEffect(() => {
    const end = Number(target) || 0;
    if (raf.current) cancelAnimationFrame(raf.current);
    t0.current = performance.now();
    const tick = (now) => {
      const elapsed = now - t0.current;
      const progress = Math.min(elapsed / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setVal(Math.round(end * ease));
      if (progress < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => { if (raf.current) cancelAnimationFrame(raf.current); };
  }, [target, duration]);
  return val;
}

function useTilt(ref, maxDeg = 8) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width  - 0.5) * maxDeg * 2;
      const y = ((e.clientY - r.top)  / r.height - 0.5) * maxDeg * 2;
      el.style.transition = 'transform .08s ease';
      el.style.transform  = `perspective(700px) rotateY(${x}deg) rotateX(${-y}deg) translateZ(10px)`;
    };
    const onLeave = () => {
      el.style.transition = 'transform .45s cubic-bezier(.22,1,.36,1)';
      el.style.transform  = '';
    };
    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => { el.removeEventListener('mousemove', onMove); el.removeEventListener('mouseleave', onLeave); };
  }, [maxDeg]);
}

// ─── Stable module-level data ─────────────────────────────────────────────────

const PARTICLE_DATA = Array.from({ length: 14 }, (_, i) => ({
  id: i, size: 3 + ((i * 37) % 7), left: ((i * 61 + 11) % 100),
  bottom: ((i * 43 + 7) % 30), dur: 11 + ((i * 29) % 14),
  delay: ((i * 53) % 12), drift: (((i * 17) % 60) - 30),
  color: ['#006d5e','#2b8f86','#0f4c81','#6b21a8','#e56e11','#0f7f3f'][i % 6],
}));

const CONFETTI_COLORS = ['#006d5e','#2b8f86','#0f4c81','#6b21a8','#e56e11','#0f7f3f'];
const CONFETTI_DATA = Array.from({ length: 26 }, (_, i) => ({
  id: i, color: CONFETTI_COLORS[i % 6],
  w: 5 + ((i * 31) % 7), h: 5 + ((i * 19) % 8),
  left: `${10 + ((i * 37) % 80)}%`, top: `${20 + ((i * 23) % 60)}%`,
  cx: `${(((i * 41) % 200) - 100)}px`, cy: `${-(40 + ((i * 29) % 80))}px`,
  cr: `${(((i * 53) % 720) - 360)}deg`, delay: `${((i * 17) % 400)}ms`,
}));

const MOCK_FORECAST_TREND = [
  { period: 'Q1 2025', expected: 4200, best: 4700, worst: 3600 },
  { period: 'Q2 2025', expected: 4600, best: 5100, worst: 3900 },
  { period: 'Q3 2025', expected: 4800, best: 5400, worst: 4200 },
  { period: 'Q4 2025', expected: 5200, best: 5900, worst: 4400 },
  { period: 'Q1 2026', expected: 5500, best: 6300, worst: 4800 },
  { period: 'Q2 2026', expected: 5800, best: 6600, worst: 5100 },
];

const MOCK_PIPELINE_TERRITORY = [
  { name: 'US East', value: 3.1 }, { name: 'US West', value: 3.2 },
  { name: 'EMEA',    value: 2.2 }, { name: 'APAC',     value: 2.5 },
];

const MOCK_RECENT_ACTIVITY = [
  { event: 'Forecast Run Completed',   entity: 'REP001',     amount: '$534,000',   status: 'COMPLETED', time: '5 min ago'   },
  { event: 'Quota Adjustment Approved',entity: 'REP012',     amount: '-$50,000',   status: 'APPROVED',  time: '12 min ago'  },
  { event: 'Forecast Run Started',     entity: 'TEAM_WEST',  amount: '$2.1M',      status: 'RUNNING',   time: '15 min ago'  },
  { event: 'Quota Adjustment Rejected',entity: 'REP007',     amount: '+$30,000',   status: 'REJECTED',  time: '1 hour ago'  },
  { event: 'Forecast Run Completed',   entity: 'REGION_EU',  amount: '$8.4M',      status: 'COMPLETED', time: '2 hours ago' },
];

const MOCK_ADJUSTMENTS = {
  PENDING: [
    { id:'adj_001', entity_type:'REP',    entity_id:'REP001',    old_amount:500000,  new_amount:450000,  delta:-10.0, trigger:'PIPELINE_COVERAGE', days:2, attainment:65.4, pipeline_coverage:2.1 },
    { id:'adj_002', entity_type:'REP',    entity_id:'REP007',    old_amount:520000,  new_amount:480000,  delta:-7.7,  trigger:'UNDERPERFORMANCE',  days:1, attainment:58.1, pipeline_coverage:2.3 },
    { id:'adj_003', entity_type:'REP',    entity_id:'REP015',    old_amount:620000,  new_amount:680000,  delta:9.7,   trigger:'DEMAND_SPIKE',      days:3, attainment:92.0, pipeline_coverage:3.8 },
  ],
  ESCALATED: [
    { id:'adj_004', entity_type:'TEAM',   entity_id:'TEAM_WEST', old_amount:2100000, new_amount:1900000, delta:-9.5,  trigger:'PIPELINE_COVERAGE', days:5, attainment:71.2, pipeline_coverage:2.0 },
    { id:'adj_005', entity_type:'REP',    entity_id:'REP012',    old_amount:380000,  new_amount:320000,  delta:-15.8, trigger:'UNDERPERFORMANCE',  days:7, attainment:48.6, pipeline_coverage:1.8 },
  ],
  COMPLETED: [
    { id:'adj_006', entity_type:'REP',    entity_id:'REP003',    old_amount:450000,  new_amount:480000,  delta:6.7,   trigger:'DEMAND_SPIKE',      days:0, attainment:95.3, pipeline_coverage:4.1 },
    { id:'adj_007', entity_type:'REP',    entity_id:'REP009',    old_amount:550000,  new_amount:520000,  delta:-5.5,  trigger:'PIPELINE_COVERAGE', days:0, attainment:68.2, pipeline_coverage:2.2 },
    { id:'adj_008', entity_type:'REGION', entity_id:'REGION_EU', old_amount:8400000, new_amount:8600000, delta:2.4,   trigger:'DEMAND_SPIKE',      days:0, attainment:88.7, pipeline_coverage:3.2 },
  ],
};

const MOCK_AUDIT_LOG = [
  { id:'log_a1b2c3', entity_type:'REP',    entity_id:'REP001',   adj_type:'DECREASE', old_quota:500000,  new_quota:450000,  delta:-10.0, trigger:'PIPELINE_COVERAGE', reasoning:'Pipeline coverage of 2.1x is below 2.5x threshold; Q2 trend declining',     status:'APPROVED', approved_by:'finance@savic', sap:'SYNCED'  },
  { id:'log_d4e5f6', entity_type:'REP',    entity_id:'REP015',   adj_type:'INCREASE', old_quota:620000,  new_quota:680000,  delta:9.7,   trigger:'DEMAND_SPIKE',       reasoning:'Recent win rate increased to 45%; territory showing 32% YTD growth',         status:'PENDING',  approved_by:'—',              sap:'PENDING' },
  { id:'log_g7h8i9', entity_type:'REP',    entity_id:'REP007',   adj_type:'DECREASE', old_quota:520000,  new_quota:480000,  delta:-7.7,  trigger:'UNDERPERFORMANCE',   reasoning:'Attainment at 58.1% for 8 consecutive weeks',                                status:'APPROVED', approved_by:'finance@savic', sap:'SYNCED'  },
  { id:'log_j1k213', entity_type:'TEAM',   entity_id:'TEAM_WEST',adj_type:'DECREASE', old_quota:2100000, new_quota:1900000, delta:-9.5,  trigger:'PIPELINE_COVERAGE', reasoning:'Team aggregate pipeline coverage at 2.0x below 2.5x target threshold',      status:'REJECTED', approved_by:'finance@savic', sap:'FAILED'  },
  { id:'log_m4n5o6', entity_type:'REP',    entity_id:'REP003',   adj_type:'INCREASE', old_quota:450000,  new_quota:480000,  delta:6.7,   trigger:'DEMAND_SPIKE',       reasoning:'Territory showing 32% growth YTD driven by new enterprise segment wins',    status:'APPROVED', approved_by:'finance@savic', sap:'SYNCED'  },
];

const PIE_COLORS = ['#2563eb', '#64748b', '#94a3b8', '#16a34a'];

function relativeTime(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000)     return 'just now';
  if (ms < 3_600_000)  return `${Math.floor(ms / 60_000)} min ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

function currentQuarter() {
  const now = new Date();
  const q = Math.ceil((now.getMonth() + 1) / 3);
  const yr = now.getFullYear();
  return {
    label: `Q${q} ${yr}`,
    start: new Date(yr, (q - 1) * 3, 1).toISOString().split('T')[0],
    end:   new Date(yr, q * 3, 0).toISOString().split('T')[0],
  };
}

// ─── Shared UI ────────────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => (
  <span className={`sf-badge sf-badge--${STATUS_CLASS[status] || 'gray'}`}>{status}</span>
);

const Spinner = ({ dark }) => (
  <span className={`sf-spinner${dark ? ' sf-spinner--dark' : ''}`} />
);

function Particles() {
  return (
    <div className="sf-particles" aria-hidden="true">
      {PARTICLE_DATA.map((p) => (
        <div key={p.id} className="sf-particle" style={{
          width: p.size, height: p.size,
          left: `${p.left}%`, bottom: `${p.bottom}%`,
          background: p.color,
          animationDuration: `${p.dur}s`, animationDelay: `${p.delay}s`,
          '--pdrift': `${p.drift}px`,
        }} />
      ))}
    </div>
  );
}

function Confetti() {
  return (
    <div className="sf-confetti-wrap" aria-hidden="true">
      {CONFETTI_DATA.map((p) => (
        <div key={p.id} className="sf-confetti-piece" style={{
          background: p.color, width: p.w, height: p.h,
          left: p.left, top: p.top,
          '--cx': p.cx, '--cy': p.cy, '--cr': p.cr,
          animationDelay: p.delay,
          borderRadius: p.id % 3 === 0 ? '50%' : '3px',
        }} />
      ))}
    </div>
  );
}

// ─── Shared Side Drawer ───────────────────────────────────────────────────────

function SideDrawer({ open, onClose, title, icon, children, footer }) {
  return (
    <>
      <div className={`sf-review-drawer ${open ? 'sf-review-drawer--open' : ''}`}>
        <div className="sf-review-drawer-header">
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            {icon && <span style={{ display:'flex' }}>{icon}</span>}
            <span style={{ fontWeight:700, fontSize:'.95rem', color:'#fff' }}>{title}</span>
          </div>
          <button className="sf-review-drawer-close" onClick={onClose}>
            <FiX size={15} />
          </button>
        </div>
        <div className="sf-review-drawer-body">{children}</div>
        {footer && <div className="sf-review-drawer-footer">{footer}</div>}
      </div>
      {open && <div className="sf-review-drawer-backdrop" onClick={onClose} />}
    </>
  );
}

function KpiStat({ label, countTo, format = (n) => n, sub, delay = 0 }) {
  const animated = useCountUp(countTo);
  return (
    <div className="sf-kpi-stat" style={{ '--delay': `${delay}ms` }}>
      <div className="sf-kpi-stat-label">{label}</div>
      <div className="sf-kpi-stat-value">{format(animated)}</div>
      {sub && <div className="sf-kpi-stat-sub">{sub}</div>}
    </div>
  );
}

function AttainmentRing({ attPct, size = 120 }) {
  const r = 42, circ = 2 * Math.PI * r;
  const dash = circ * Math.min(attPct / 100, 1);
  const color = attPct >= 100 ? '#0f7f3f' : attPct >= 70 ? '#e56e11' : '#a42f20';
  return (
    <div className="sf-ring-wrap" style={{ width: size }}>
      <svg width={size} height={size} viewBox="0 0 100 100">
        <circle cx="50" cy="50" r={r} fill="none" stroke="#e2e8f0" strokeWidth="9" />
        <circle cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth="9"
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          style={{ transform:'rotate(-90deg)', transformOrigin:'50% 50%',
                   transition:'stroke-dasharray 1s cubic-bezier(.22,1,.36,1)' }} />
        <text x="50" y="50" textAnchor="middle" dominantBaseline="central"
          style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:'20px', fontWeight:800, fill:color }}>
          {Math.round(attPct)}%
        </text>
      </svg>
      <span className="sf-ring-label">Attainment</span>
    </div>
  );
}

// ─── Dashboard tab ────────────────────────────────────────────────────────────

function DashboardTab() {
  const [stats,         setStats]         = useState(null);
  const [forecastTrend, setForecastTrend] = useState(MOCK_FORECAST_TREND);
  const [terrItems,     setTerrItems]     = useState([]);
  const [activity,      setActivity]      = useState(MOCK_RECENT_ACTIVITY);
  const [loading,       setLoading]       = useState(true);
  const [entityNameMap, setEntityNameMap] = useState({});

  const Q = useMemo(() => currentQuarter(), []);

  const pipelineData = useMemo(
    () => terrItems.length
      ? terrItems.map(r => ({
          name:  entityNameMap[r.entity_id] || r.entity_id,
          value: Number(r.pipeline_coverage_ratio || 0),
        }))
      : MOCK_PIPELINE_TERRITORY,
    [terrItems, entityNameMap]
  );

  useEffect(() => {
    loadDashboard();
    api.getQuotaEntities()
      .then(data => {
        const map = {};
        (data.regions || []).forEach(e => { map[e.entity_id] = e.entity_name; });
        (data.reps    || []).forEach(e => { map[e.entity_id] = e.entity_name; });
        setEntityNameMap(map);
      })
      .catch(() => {});
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    const results = await Promise.allSettled([
      api.listForecasts({ limit: 1 }),                                               // [0] total
      api.listForecasts({ status: 'RUNNING',   limit: 1 }),                          // [1] running
      api.listWorkflows({ status: 'PENDING',   limit: 1 }),                          // [2] pending adj
      api.listWorkflows({ status: 'ESCALATED', limit: 1 }),                          // [3] escalated
      api.listPerformance({ period_start: Q.start, period_end: Q.end, limit: 500 }), // [4] attainment
      api.getPerformanceExceptions({ limit: 1 }),                                    // [5] underperforming
      api.listForecasts({ limit: 500 }),                                             // [6] chart data
      api.listPerformance({ entity_type: 'REGION', limit: 100 }),                    // [7] territory
      api.listForecasts({ limit: 10 }),                                              // [8] recent forecasts
      api.listWorkflows({ limit: 10 }),                                              // [9] recent workflows
    ]);

    const val   = (i) => results[i].status === 'fulfilled' ? results[i].value : null;
    const tot   = (i) => val(i)?.total ?? (Array.isArray(val(i)) ? val(i).length : 0);
    const items = (i) => val(i)?.items ?? (Array.isArray(val(i)) ? val(i) : []);

    const perfItems = items(4);
    const avgAtt = perfItems.length
      ? perfItems.reduce((s, r) => s + (Number(r.attainment_pct) || 0), 0) / perfItems.length
      : 0;

    setStats({
      totalForecasts:   tot(0),
      runningForecasts: tot(1),
      pendingAdj:       tot(2),
      escalatedAdj:     tot(3),
      avgAttainment:    Math.round(avgAtt * 10) / 10,
      underperforming:  tot(5),
    });

    const fcItems = items(6);
    if (fcItems.length) {
      const map = {};
      fcItems.forEach(f => {
        const q = fmtQuarter(f.forecast_period_start);
        if (!q || q === '—') return;
        if (!map[q]) map[q] = { period: q, expected: 0, best: 0, worst: 0 };
        map[q].expected += Number(f.forecast_value_expected || 0) / 1000;
        map[q].best     += Number(f.forecast_value_best     || 0) / 1000;
        map[q].worst    += Number(f.forecast_value_worst    || 0) / 1000;
      });
      const trend = Object.values(map)
        .sort((a, b) => a.period.localeCompare(b.period)).slice(-6)
        .map(q => ({ period: q.period, expected: Math.round(q.expected), best: Math.round(q.best), worst: Math.round(q.worst) }));
      if (trend.length) setForecastTrend(trend);
    }

    const terr = items(7);
    if (terr.length) setTerrItems(terr);

    const fcAct = items(8).map(f => ({
      event:  f.status === 'RUNNING' ? 'Forecast Run Started' : 'Forecast Run Completed',
      entity: f.dimension_id || '—',
      amount: fmt(f.forecast_value_expected, f.currency_code),
      status: f.status || 'COMPLETED',
      time:   relativeTime(f.created_at),
      ts:     new Date(f.created_at || 0).getTime(),
    }));
    const wfAct = items(9).map(w => ({
      event:  w.status === 'APPROVED' ? 'Quota Adjustment Approved' : w.status === 'REJECTED' ? 'Quota Adjustment Rejected' : 'Quota Adjustment Pending',
      entity: w.entity_id || '—',
      amount: w.delta_pct != null ? `${Number(w.delta_pct) > 0 ? '+' : ''}${Number(w.delta_pct).toFixed(1)}%` : '—',
      status: w.status || 'PENDING',
      time:   relativeTime(w.created_at),
      ts:     new Date(w.created_at || 0).getTime(),
    }));
    const merged = [...fcAct, ...wfAct].sort((a, b) => b.ts - a.ts).slice(0, 10);
    if (merged.length) setActivity(merged);

    setLoading(false);
  };

  if (loading && !stats) {
    return <div className="sf-loading"><Spinner dark /> Loading dashboard…</div>;
  }

  const s = stats || { totalForecasts: 0, runningForecasts: 0, pendingAdj: 0, escalatedAdj: 0, avgAttainment: 0, underperforming: 0 };

  return (
    <div className="sf-tab-content">
      <div className="sf-kpi-strip">
        <KpiStat label="Active Forecasts"     countTo={s.totalForecasts}   format={n => n}       sub={`${s.runningForecasts} running now`}   delay={0}   />
        <KpiStat label="Quota Adj. Pending"   countTo={s.pendingAdj}       format={n => n}       sub={`${s.escalatedAdj} escalated`}         delay={60}  />
        <KpiStat label="Avg Attainment"       countTo={s.avgAttainment}    format={n => `${n}%`} sub={Q.label}                               delay={120} />
        <KpiStat label="Underperforming Reps" countTo={s.underperforming}  format={n => n}       sub="Below 70% threshold"                   delay={180} />
      </div>

      <div className="sf-dash-chart-row">
        <div className="sf-dash-chart-card">
          <div className="sf-dash-chart-title">Forecast vs Actual Revenue — Last 6 Quarters</div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={forecastTrend} margin={{ top:8, right:16, left:0, bottom:0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="period" tick={{ fontSize:11 }} />
              <YAxis tick={{ fontSize:11 }} tickFormatter={v => `${v}K`} />
              <Tooltip formatter={(v) => `$${(v * 1000).toLocaleString()}`} />
              <Legend iconType="circle" wrapperStyle={{ fontSize:12 }} />
              <Line type="monotone" dataKey="expected" name="Expected"   stroke="#2563eb" strokeWidth={2} dot={{ r:3 }} />
              <Line type="monotone" dataKey="best"     name="Best Case"  stroke="#16a34a" strokeWidth={2} dot={{ r:3 }} strokeDasharray="4 2" />
              <Line type="monotone" dataKey="worst"    name="Worst Case" stroke="#dc2626" strokeWidth={2} dot={{ r:3 }} strokeDasharray="4 2" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="sf-dash-chart-card">
          <div className="sf-dash-chart-title">Pipeline Coverage by Territory</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={pipelineData} margin={{ top:8, right:24, left:0, bottom:0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize:11 }} />
              <YAxis tick={{ fontSize:11 }} domain={[0, 4]} />
              <Tooltip formatter={(v) => `${v}x`} />
              <ReferenceLine y={2.5} stroke="#ef4444" strokeDasharray="4 4"
                label={{ value:'2.5x', position:'right', fontSize:11, fill:'#ef4444' }} />
              <Bar dataKey="value" name="Coverage" fill="#3b82f6" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="sf-dash-activity">
        <div className="sf-dash-activity-title">Recent Activity</div>
        <div className="sf-table-wrap">
          <table className="sf-table">
            <thead>
              <tr><th>Event</th><th>Entity</th><th>Amount</th><th>Status</th><th>Time</th></tr>
            </thead>
            <tbody>
              {activity.map((r, i) => (
                <tr key={i}>
                  <td>{r.event}</td>
                  <td><code style={{ fontSize:'.8rem', background:'#f1f5f9', padding:'2px 6px', borderRadius:4 }}>{r.entity}</code></td>
                  <td>{r.amount}</td>
                  <td><StatusBadge status={r.status} /></td>
                  <td style={{ color:'#94a3b8', fontSize:'.82rem' }}>{r.time}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Forecast Detail view ─────────────────────────────────────────────────────

function ForecastDetail({ forecast: initial, onBack, onRequestAdj }) {
  const [forecast, setForecast] = useState(initial);

  useEffect(() => {
    if (initial.forecast_id) {
      api.getForecast(initial.forecast_id).then(setForecast).catch(() => {});
    }
  }, [initial.forecast_id]);

  const h = stableHash(forecast.forecast_id || 'x');
  const confPct = Math.round(Number(forecast.confidence_score || 0) * 100);
  const currency = forecast.currency_code || 'USD';
  const expected = Number(forecast.forecast_value_expected || 0);

  const ensembleRaw = [
    { name: 'Time Series Component',    color:'#3b82f6', w: 40 + (h % 20) },
    { name: 'Pipeline Component',       color:'#8b5cf6', w: 25 + ((h >> 4) % 15) },
    { name: 'External Signals (GDP/PMI)',color:'#10b981', w: 10 + ((h >> 8) % 10) },
  ];
  const ensTotal = ensembleRaw.reduce((s, e) => s + e.w, 0);
  const ensemble = ensembleRaw.map(e => ({ ...e, pct: Math.round(e.w / ensTotal * 100) }));

  const monthlyData = Array.from({ length: 12 }, (_, i) => {
    const base = expected / 12;
    const variation = 1 + (((h >> (i * 2)) & 0x1F) - 15) / 100;
    return {
      month: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][i],
      revenue: Math.max(0, Math.round(base * variation)),
    };
  });

  const totalPipeline = expected * (2.1 + ((h % 8) / 10));
  const pieSections = [
    { name:'New Business', value: Math.round(totalPipeline * 0.35) },
    { name:'Expansion',    value: Math.round(totalPipeline * 0.27) },
    { name:'Renewal',      value: Math.round(totalPipeline * 0.22) },
    { name:'Upsell',       value: Math.round(totalPipeline * 0.16) },
  ];

  const gdp  = (1.8 + ((h % 120) / 100)).toFixed(1);
  const pmi  = 48 + ((h >> 6) % 12);
  const hasAnomaly = (h % 3) === 0;

  return (
    <div className="sf-tab-content">
      <div className="sf-detail-header">
        <button className="sf-btn sf-btn--ghost" onClick={onBack}>
          <FiArrowLeft size={14} /> Back to Forecasts
        </button>
        <h2 className="sf-detail-title">
          Forecast: {forecast.dimension_id} — {fmtQuarter(forecast.forecast_period_start)}
        </h2>
      </div>

      {/* Confidence ring */}
      <div className="sf-detail-conf-wrap">
        <div style={{ textAlign:'center' }}>
          <svg width={160} height={160} viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="42" fill="none" stroke="#e2e8f0" strokeWidth="7" />
            <circle cx="50" cy="50" r="42" fill="none"
              stroke={confPct >= 80 ? '#16a34a' : confPct >= 70 ? '#e56e11' : '#dc2626'}
              strokeWidth="7"
              strokeDasharray={`${2 * Math.PI * 42 * Math.min(confPct / 100, 1)} ${2 * Math.PI * 42}`}
              strokeLinecap="round"
              style={{ transform:'rotate(-90deg)', transformOrigin:'50% 50%',
                       transition:'stroke-dasharray 1.2s cubic-bezier(.22,1,.36,1)' }} />
            <text x="50" y="46" textAnchor="middle" dominantBaseline="central"
              style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:'22px', fontWeight:800,
                       fill: confPct >= 80 ? '#16a34a' : confPct >= 70 ? '#e56e11' : '#dc2626' }}>
              {confPct}%
            </text>
          </svg>
          <div style={{ fontSize:'.82rem', color:'#64748b', marginTop:6 }}>Confidence Score</div>
        </div>
      </div>

      {/* Scenario cards */}
      <div className="sf-detail-scenarios">
        <div className="sf-detail-sc-card sf-detail-sc-card--best">
          <div className="sf-detail-sc-label">Best Case</div>
          <div className="sf-detail-sc-val" style={{ color:'#16a34a' }}>{fmt(forecast.forecast_value_best, currency)}</div>
        </div>
        <div className="sf-detail-sc-card sf-detail-sc-card--expected">
          <div className="sf-detail-sc-label">Expected</div>
          <div className="sf-detail-sc-val" style={{ color:'#2563eb' }}>{fmt(forecast.forecast_value_expected, currency)}</div>
        </div>
        <div className="sf-detail-sc-card sf-detail-sc-card--worst">
          <div className="sf-detail-sc-label">Worst Case</div>
          <div className="sf-detail-sc-val" style={{ color:'#dc2626' }}>{fmt(forecast.forecast_value_worst, currency)}</div>
        </div>
      </div>

      {/* Ensemble breakdown */}
      <div className="sf-detail-section">
        <div className="sf-detail-section-title">Ensemble Breakdown</div>
        {ensemble.map((e) => (
          <div key={e.name} className="sf-ensemble-row">
            <div className="sf-ensemble-label">{e.name}</div>
            <div className="sf-ensemble-track">
              <div className="sf-ensemble-fill" style={{ width:`${e.pct}%`, background:e.color }} />
            </div>
            <div className="sf-ensemble-pct">{e.pct}%</div>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div className="sf-dash-chart-row">
        <div className="sf-dash-chart-card">
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={monthlyData} margin={{ top:8, right:12, left:0, bottom:0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize:10 }} />
              <YAxis tick={{ fontSize:10 }} tickFormatter={v => `${(v/1000).toFixed(0)}K`} />
              <Tooltip formatter={(v) => fmt(v, currency)} />
              <Line type="monotone" dataKey="revenue" stroke="#2563eb" strokeWidth={2} dot={{ r:2 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="sf-dash-chart-card" style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={pieSections} cx="50%" cy="50%" innerRadius={55} outerRadius={80}
                dataKey="value" nameKey="name">
                {pieSections.map((_, idx) => <Cell key={idx} fill={PIE_COLORS[idx]} />)}
              </Pie>
              <Tooltip formatter={(v) => fmt(v, currency)} />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ fontSize:'.8rem', color:'#64748b', marginTop:4 }}>
            Total Pipeline: {fmtM(totalPipeline, currency)}
          </div>
        </div>
      </div>

      {/* External Signals */}
      <div className="sf-detail-section">
        <div className="sf-detail-section-title">External Signals</div>
        <div className="sf-detail-signals">
          <div className="sf-detail-signal">
            <div className="sf-signal-label">GDP Growth</div>
            <div className="sf-signal-val" style={{ color: Number(gdp) > 0 ? '#16a34a' : '#dc2626' }}>
              {gdp}%
            </div>
          </div>
          <div className="sf-detail-signal">
            <div className="sf-signal-label">PMI Index</div>
            <div className="sf-signal-val" style={{ color: pmi >= 50 ? '#16a34a' : '#dc2626' }}>
              {pmi.toFixed(1)} <span style={{ fontSize:'.72rem', opacity:.8 }}>({pmi >= 50 ? 'EXPANSION' : 'CONTRACTION'})</span>
            </div>
          </div>
        </div>
      </div>

      {/* Anomaly alert */}
      {hasAnomaly && (
        <div className="sf-detail-anomaly">
          <FiAlertTriangle size={14} />
          <div>
            <strong>Anomalies Detected:</strong>
            <span> Phantom pipeline detected: 2 deals stagnant &gt;90 days</span>
          </div>
        </div>
      )}

      <div style={{ display:'flex', justifyContent:'flex-end', marginTop:24 }}>
        <button className="sf-btn sf-btn--primary" onClick={onRequestAdj}>
          Request Quota Adjustment
        </button>
      </div>
    </div>
  );
}

// ─── Forecasts tab ────────────────────────────────────────────────────────────

function ForecastsTab() {
  const [forecasts,        setForecasts]        = useState([]);
  const [total,            setTotal]            = useState(0);
  const [loading,          setLoading]          = useState(false);
  const [error,            setError]            = useState('');
  const [filters,          setFilters]          = useState({ dimension_type:'', status:'' });
  const [showForm,         setShowForm]         = useState(false);
  const [form,             setForm]             = useState({
    dimension_type:'REGION', dimension_ids:'', period_start:'', period_end:'',
    scenarios:['BASE','BEST','WORST'],
  });
  const [generating,       setGenerating]       = useState(false);
  const [runStatus,        setRunStatus]        = useState(null);
  const [selectedForecast, setSelectedForecast] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    fetchForecasts({});
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const fetchForecasts = async (overrideFilters) => {
    setLoading(true); setError('');
    const f = overrideFilters !== undefined ? overrideFilters : filters;
    try {
      const data = await api.listForecasts({
        dimension_type: f.dimension_type || undefined,
        limit: 50,
      });
      setForecasts(data.items || data || []);
      setTotal(data.total || (data.items || data || []).length);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const startPolling = (runId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const run = await api.getForecastRun(runId);
        setRunStatus(run);
        if (run.status === 'COMPLETED' || run.status === 'FAILED') {
          clearInterval(pollRef.current); pollRef.current = null;
          if (run.status === 'COMPLETED') fetchForecasts(filters);
        }
      } catch (_) { clearInterval(pollRef.current); pollRef.current = null; }
    }, 3000);
  };

  const handleGenerate = async (e) => {
    e.preventDefault(); setError(''); setGenerating(true);
    try {
      const ids = form.dimension_ids.split(',').map(s => s.trim()).filter(Boolean);
      const result = await api.generateForecast({
        dimension_type: form.dimension_type, dimension_ids: ids,
        period_start: form.period_start, period_end: form.period_end,
        scenarios: form.scenarios,
      });
      setRunStatus({ id: result.forecast_run_id || result.run_id, status: result.status });
      setShowForm(false);
      if (result.forecast_run_id || result.run_id) startPolling(result.forecast_run_id || result.run_id);
    } catch (e) { setError(e.message); }
    finally { setGenerating(false); }
  };

  const toggleScenario = (s) =>
    setForm(p => ({ ...p, scenarios: p.scenarios.includes(s) ? p.scenarios.filter(x => x !== s) : [...p.scenarios, s] }));

  if (selectedForecast) {
    return (
      <ForecastDetail
        forecast={selectedForecast}
        onBack={() => setSelectedForecast(null)}
        onRequestAdj={() => setSelectedForecast(null)}
      />
    );
  }

  return (
    <div className="sf-tab-content">
      <div className="sf-forecast-tbl-bar">
        <div className="sf-filters" style={{ flex:1 }}>
          <select className="sf-select sf-select--sm" value={filters.dimension_type}
            onChange={e => setFilters(p => ({ ...p, dimension_type: e.target.value }))}>
            <option value="">All Types</option>
            {['REGION','REP','PRODUCT','CUSTOMER'].map(t => <option key={t}>{t}</option>)}
          </select>
          <button className="sf-btn sf-btn--primary sf-btn--sm" onClick={() => fetchForecasts(filters)}>
            <FiSearch size={13} /> Search
          </button>
          <button className="sf-btn sf-btn--ghost sf-btn--sm" onClick={() => fetchForecasts(filters)}>
            <FiRefreshCw size={13} />
          </button>
        </div>
        <button className="sf-btn sf-btn--primary sf-btn--sm" onClick={() => setShowForm(v => !v)}>
          <FiPlus size={13} /> Generate New Forecast
        </button>
      </div>

      {showForm && (
        <div className="sf-panel" style={{ marginBottom:16 }}>
          <form className="sf-form" onSubmit={handleGenerate}>
            <div className="sf-form-row">
              <label className="sf-label">Dimension Type
                <select className="sf-select" value={form.dimension_type}
                  onChange={e => setForm(p => ({ ...p, dimension_type: e.target.value }))}>
                  {['REGION','REP','PRODUCT','CUSTOMER'].map(t => <option key={t}>{t}</option>)}
                </select>
              </label>
              <label className="sf-label">Dimension IDs (comma-separated)
                <input className="sf-input" value={form.dimension_ids} required
                  placeholder="e.g. 59, 60"
                  onChange={e => setForm(p => ({ ...p, dimension_ids: e.target.value }))} />
              </label>
            </div>
            <div className="sf-form-row">
              <label className="sf-label">Period Start
                <input className="sf-input" type="date" value={form.period_start} required
                  onChange={e => setForm(p => ({ ...p, period_start: e.target.value }))} />
              </label>
              <label className="sf-label">Period End
                <input className="sf-input" type="date" value={form.period_end} required
                  onChange={e => setForm(p => ({ ...p, period_end: e.target.value }))} />
              </label>
            </div>
            <label className="sf-label">Scenarios
              <div className="sf-checkbox-group">
                {['BASE','BEST','WORST'].map(s => (
                  <label key={s} className="sf-checkbox-label">
                    <input type="checkbox" checked={form.scenarios.includes(s)} onChange={() => toggleScenario(s)} /> {s}
                  </label>
                ))}
              </div>
            </label>
            {error && <div className="sf-error">{error}</div>}
            <div className="sf-form-actions">
              <button type="submit" className="sf-btn sf-btn--primary" disabled={generating}>
                {generating ? <><Spinner /> Generating…</> : 'Generate'}
              </button>
              <button type="button" className="sf-btn sf-btn--ghost" onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {runStatus && (
        <div className={`sf-run-card sf-run-card--${(runStatus.status || '').toLowerCase()}`} style={{ marginBottom:12 }}>
          <div className="sf-run-card-left">
            {(runStatus.status === 'PENDING' || runStatus.status === 'RUNNING') && <Spinner dark />}
            <div>
              <div className="sf-run-id">Run: {runStatus.id}</div>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:4 }}>
                <StatusBadge status={runStatus.status} />
              </div>
            </div>
          </div>
          <button className="sf-btn sf-btn--ghost sf-btn--sm"
            onClick={() => api.getForecastRun(runStatus.id).then(setRunStatus).catch(() => {})}>
            <FiRefreshCw size={13} />
          </button>
        </div>
      )}

      {error && !showForm && <div className="sf-error">{error}</div>}

      {loading ? (
        <div className="sf-loading"><Spinner dark /> Loading forecasts…</div>
      ) : (
        <>
          <div className="sf-results-meta">{total} forecast{total !== 1 ? 's' : ''}</div>
          <div className="sf-table-wrap">
            <table className="sf-table">
              <thead>
                <tr>
                  <th>Forecast ID</th><th>Type</th><th>Dimension</th><th>Period</th>
                  <th>Expected Revenue</th><th>Confidence</th><th>Status</th>
                  <th>Created By</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {forecasts.map((f) => {
                  const h = stableHash(f.forecast_id || '');
                  const confPct = Math.round(Number(f.confidence_score || 0) * 100);
                  const confColor = confPct >= 80 ? 'green' : confPct >= 70 ? 'amber' : 'red';
                  return (
                    <tr key={f.forecast_id}>
                      <td style={{ fontFamily:'monospace', fontSize:'.78rem', color:'#2563eb' }}>
                        {(f.forecast_id || '').slice(0, 16)}
                      </td>
                      <td>
                        <span className={`sf-dim-badge ${DIM_BADGE_CLASS[f.dimension_type] || 'sf-dim-default'}`}>
                          {f.dimension_type}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontWeight:600, fontSize:'.82rem' }}>{f.dimension_name || f.dimension_id}</div>
                        {f.dimension_name && (
                          <div style={{ fontFamily:'monospace', fontSize:'.72rem', color:'#94a3b8' }}>{f.dimension_id}</div>
                        )}
                      </td>
                      <td>{fmtQuarter(f.forecast_period_start)}</td>
                      <td><strong>{fmt(f.forecast_value_expected, f.currency_code)}</strong></td>
                      <td>
                        <span className={`sf-badge sf-badge--${confColor}`}>{confPct}%</span>
                      </td>
                      <td><StatusBadge status={f.status || 'COMPLETED'} /></td>
                      <td style={{ fontSize:'.8rem', color:'#64748b' }}>
                        {f.created_by || 'admin@savic'}
                      </td>
                      <td>
                        <button className="sf-btn sf-btn--ghost sf-btn--xs"
                          onClick={() => setSelectedForecast(f)}>
                          <FiEye size={12} /> View
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {forecasts.length === 0 && (
                  <tr><td colSpan={9} className="sf-empty-cell">No forecasts found. Generate one above.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Quarter period options (Q1 2024 → Q4 2027) ──────────────────────────────
const QUARTER_PERIODS = (() => {
  const list = [];
  for (let y = 2024; y <= 2027; y++) {
    for (let q = 1; q <= 4; q++) {
      const startMonth = (q - 1) * 3;
      const endMonth   = startMonth + 2;
      const start = new Date(y, startMonth, 1);
      const end   = new Date(y, endMonth + 1, 0);
      list.push({
        label:        `Q${q} ${y}`,
        period_start: start.toISOString().slice(0, 10),
        period_end:   end.toISOString().slice(0, 10),
      });
    }
  }
  return list;
})();

// ─── Quotas tab ───────────────────────────────────────────────────────────────

function QuotasTab() {
  const [quotas,        setQuotas]        = useState([]);
  const [total,         setTotal]         = useState(0);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState('');
  const [filters,       setFilters]       = useState({ entity_type:'', entity_id:'', status:'', period:'' });
  const [entities,      setEntities]      = useState({ reps:[], regions:[] });
  const [showCreate,    setShowCreate]    = useState(false);
  const [createForm,    setCreateForm]    = useState({
    entity_type:'REP', entity_id:'', period_start:'', period_end:'',
    quota_amount:'', currency_code:'USD',
  });
  const [creating,      setCreating]      = useState(false);
  const [adjustTarget,  setAdjustTarget]  = useState(null);
  const [adjustForm,    setAdjustForm]    = useState({
    adjustment_type:'DECREASE', trigger_reason:'FORECAST_DELTA',
    new_quota_amount:'', effective_date:'', rationale:'',
  });
  const [adjusting,     setAdjusting]     = useState(false);
  const [adjustResult,  setAdjustResult]  = useState(null);
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveComment,setApproveComment]= useState('');
  const [approving,     setApproving]     = useState(false);
  const [approveError,  setApproveError]  = useState('');
  const [rejectTarget,  setRejectTarget]  = useState(null);
  const [rejectComment, setRejectComment] = useState('');
  const [rejecting,     setRejecting]     = useState(false);
  const [rejectError,   setRejectError]   = useState('');
  const [sapSyncingId,  setSapSyncingId]  = useState(null);
  const [sapSyncMsg,    setSapSyncMsg]    = useState('');

  useEffect(() => {
    fetchQuotas({});
    api.getQuotaEntities()
      .then(data => setEntities({ reps: data.reps || [], regions: data.regions || [] }))
      .catch(() => {});
  }, []);

  const fetchQuotas = async (overrideFilters) => {
    setLoading(true); setError('');
    const f = overrideFilters !== undefined ? overrideFilters : filters;
    try {
      const qp = QUARTER_PERIODS.find(q => q.label === f.period);
      const data = await api.listQuotas({
        entity_type:  f.entity_type  || undefined,
        entity_id:    f.entity_id    || undefined,
        status:       f.status       || undefined,
        period_start: qp?.period_start || undefined,
        period_end:   qp?.period_end   || undefined,
        limit: 50,
      });
      setQuotas(data.items || data || []);
      setTotal(data.total || (data.items || data || []).length);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault(); setCreating(true); setError('');
    try {
      await api.createQuota({ ...createForm, quota_amount: Number(createForm.quota_amount), allocation_method: 'TOP_DOWN' });
      setShowCreate(false);
      setCreateForm({ entity_type:'REP', entity_id:'', period_start:'', period_end:'', quota_amount:'', currency_code:'USD' });
      fetchQuotas(filters);
    } catch (e) { setError(e.message); }
    finally { setCreating(false); }
  };

  const handleAdjust = async (e) => {
    e.preventDefault(); setAdjusting(true); setError('');
    try {
      const result = await api.createAdjustment({
        quota_id: adjustTarget.quota_id,
        adjustment_type: adjustForm.adjustment_type,
        trigger_reason: adjustForm.trigger_reason,
        new_quota_amount: Number(adjustForm.new_quota_amount),
        effective_date: adjustForm.effective_date,
        rationale: adjustForm.rationale,
      });
      setAdjustResult(result);
    } catch (e) { setError(e.message); }
    finally { setAdjusting(false); }
  };

  const handleApprove = async () => {
    setApproving(true); setApproveError('');
    try {
      await api.approveQuota(approveTarget.quota_id, {
        decision: 'APPROVED',
        approver_comment: approveComment.trim() || 'Approved',
        conditions: [],
      });
      setApproveTarget(null); setApproveComment(''); fetchQuotas(filters);
    } catch (e) { setApproveError(e.message); }
    finally { setApproving(false); }
  };

  const handleReject = async () => {
    setRejecting(true); setRejectError('');
    try {
      await api.rejectQuota(rejectTarget.quota_id, {
        decision: 'REJECTED',
        approver_comment: rejectComment.trim() || 'Rejected',
        conditions: [],
      });
      setRejectTarget(null); setRejectComment(''); fetchQuotas(filters);
    } catch (e) { setRejectError(e.message); }
    finally { setRejecting(false); }
  };

  const handleSapSync = async (quotaId) => {
    setSapSyncingId(quotaId); setSapSyncMsg('');
    try {
      const res = await api.sapSync(quotaId);
      setSapSyncMsg(res?.sap_writeback?.status || 'Sync triggered');
      setTimeout(() => setSapSyncMsg(''), 4000);
    } catch (e) { setSapSyncMsg(`Error: ${e.message}`); setTimeout(() => setSapSyncMsg(''), 4000); }
    finally { setSapSyncingId(null); fetchQuotas(filters); }
  };

  const openAdjust = (q) => {
    setAdjustTarget(q); setAdjustResult(null);
    setAdjustForm({ adjustment_type:'DECREASE', trigger_reason:'FORECAST_DELTA',
                   new_quota_amount: q.quota_amount || '', effective_date:'', rationale:'' });
  };

  const entityOptions = createForm.entity_type === 'REP' ? entities.reps : createForm.entity_type === 'REGION' ? entities.regions : [];

  const totalActive = quotas.filter(q => q.status === 'ACTIVE' || q.status === 'APPROVED')
    .reduce((s, q) => s + Number(q.quota_amount || 0), 0);
  const activeCount = quotas.filter(q => q.status === 'ACTIVE' || q.status === 'APPROVED').length;

  const sapBadge = (status) => {
    if (!status) return <span className="sf-badge sf-badge--gray">—</span>;
    if (status === 'SYNCED') return <span className="sf-badge sf-badge--green">✓ Synced</span>;
    if (status === 'FAILED') return <span className="sf-badge sf-badge--red">✗ Failed</span>;
    if (status === 'PENDING') return <span className="sf-badge sf-badge--amber">⏳ Pending</span>;
    return <span className="sf-badge sf-badge--gray">{status}</span>;
  };

  return (
    <div className="sf-tab-content">
      <div className="sf-filters">
        <select className="sf-select sf-select--sm" value={filters.period}
          onChange={e => setFilters(p => ({ ...p, period: e.target.value }))}>
          <option value="">All Periods</option>
          {QUARTER_PERIODS.map(q => <option key={q.label} value={q.label}>{q.label}</option>)}
        </select>
        <select className="sf-select sf-select--sm" value={filters.entity_type}
          onChange={e => setFilters(p => ({ ...p, entity_type: e.target.value }))}>
          <option value="">All Types</option>
          {['REP','REGION','PRODUCT','CUSTOMER'].map(t => <option key={t}>{t}</option>)}
        </select>
        <select className="sf-select sf-select--sm" value={filters.status}
          onChange={e => setFilters(p => ({ ...p, status: e.target.value }))}>
          <option value="">All Status</option>
          {['DRAFT','ACTIVE','APPROVED','REJECTED'].map(s => <option key={s}>{s}</option>)}
        </select>
        <button className="sf-btn sf-btn--primary sf-btn--sm" onClick={() => fetchQuotas(filters)}>
          <FiSearch size={13} /> Search
        </button>
        <button className="sf-btn sf-btn--ghost sf-btn--sm" onClick={() => fetchQuotas(filters)}>
          <FiRefreshCw size={13} />
        </button>
        <button className="sf-btn sf-btn--primary sf-btn--sm sf-ml-auto" onClick={() => setShowCreate(true)}>
          <FiPlus size={13} /> Create Quota
        </button>
      </div>

      {totalActive > 0 && (
        <div className="sf-quota-summary-bar">
          Total Active Quota: <strong>{fmt(totalActive)}</strong> across {activeCount} entities
        </div>
      )}

      {error && <div className="sf-error">{error}</div>}
      {sapSyncMsg && (
        <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:8,
          padding:'8px 14px', fontSize:'.83rem', color:'#15803d', marginBottom:8 }}>
          SAP Sync: {sapSyncMsg}
        </div>
      )}

      {/* ── Create Quota Drawer ── */}
      <SideDrawer
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="Create Quota"
        icon={<FiPlus size={15} color="#fff" />}
        footer={
          <>
            <button className="sf-btn sf-btn--ghost" onClick={() => setShowCreate(false)}>Cancel</button>
            <button form="create-quota-form" type="submit" className="sf-btn sf-btn--primary" disabled={creating}>
              {creating ? <><Spinner /> Creating…</> : 'Create Quota'}
            </button>
          </>
        }
      >
        <form id="create-quota-form" className="sf-form" onSubmit={handleCreate} style={{ padding:0 }}>
          <label className="sf-label">Entity Type
            <select className="sf-select" value={createForm.entity_type}
              onChange={e => setCreateForm(p => ({ ...p, entity_type: e.target.value, entity_id:'' }))}>
              {['REP','REGION','PRODUCT','CUSTOMER'].map(t => <option key={t}>{t}</option>)}
            </select>
          </label>
          <label className="sf-label">Entity
            {entityOptions.length > 0 ? (
              <select className="sf-select" value={createForm.entity_id} required
                onChange={e => setCreateForm(p => ({ ...p, entity_id: e.target.value }))}>
                <option value="">Select…</option>
                {entityOptions.map(en => (
                  <option key={en.entity_id} value={en.entity_id}>
                    {en.entity_name} ({en.entity_id})
                  </option>
                ))}
              </select>
            ) : (
              <input className="sf-input" value={createForm.entity_id} required
                placeholder="e.g. 8000000013"
                onChange={e => setCreateForm(p => ({ ...p, entity_id: e.target.value }))} />
            )}
          </label>
          <label className="sf-label">Period Start
            <input className="sf-input" type="date" value={createForm.period_start} required
              onChange={e => setCreateForm(p => ({ ...p, period_start: e.target.value }))} />
          </label>
          <label className="sf-label">Period End
            <input className="sf-input" type="date" value={createForm.period_end} required
              onChange={e => setCreateForm(p => ({ ...p, period_end: e.target.value }))} />
          </label>
          <label className="sf-label">Quota Amount
            <input className="sf-input" type="number" min="0" value={createForm.quota_amount} required
              onChange={e => setCreateForm(p => ({ ...p, quota_amount: e.target.value }))} />
          </label>
          <label className="sf-label">Currency
            <select className="sf-select" value={createForm.currency_code}
              onChange={e => setCreateForm(p => ({ ...p, currency_code: e.target.value }))}>
              <option>USD</option><option>EUR</option><option>INR</option>
            </select>
          </label>
          {error && <div className="sf-error">{error}</div>}
        </form>
      </SideDrawer>

      {/* ── Adjust Quota Drawer ── */}
      <SideDrawer
        open={!!adjustTarget}
        onClose={() => { setAdjustTarget(null); setAdjustResult(null); }}
        title="Adjust Quota"
        icon={<FiSliders size={15} color="#fff" />}
        footer={!adjustResult ? (
          <>
            <button className="sf-btn sf-btn--ghost" onClick={() => setAdjustTarget(null)}>Cancel</button>
            <button form="adjust-quota-form" type="submit" className="sf-btn sf-btn--primary" disabled={adjusting}>
              {adjusting ? <><Spinner /> Submitting…</> : 'Submit Adjustment'}
            </button>
          </>
        ) : undefined}
      >
        {adjustResult ? (
          <div className="sf-adjust-result" style={{ padding:'32px 0' }}>
            <Confetti />
            <FiCheck size={36} className="sf-adjust-result-icon" />
            <div className="sf-adjust-result-title">Adjustment Submitted</div>
            {adjustResult.adjustment_id && (
              <div className="sf-adjust-result-row"><span>ID:</span><code>{adjustResult.adjustment_id}</code></div>
            )}
            {adjustResult.workflow_id && (
              <div className="sf-adjust-result-row"><span>Workflow:</span><code>{adjustResult.workflow_id}</code></div>
            )}
            {adjustResult.delta_pct != null && (
              <div className="sf-adjust-result-row">
                <span>Delta:</span>
                <strong style={{ color: adjustResult.delta_pct < 0 ? '#dc2626' : '#16a34a' }}>
                  {adjustResult.delta_pct > 0 ? '+' : ''}{Number(adjustResult.delta_pct).toFixed(2)}%
                </strong>
              </div>
            )}
            <button className="sf-btn sf-btn--primary"
              onClick={() => { setAdjustTarget(null); setAdjustResult(null); fetchQuotas(filters); }}>Done</button>
          </div>
        ) : adjustTarget && (
          <>
            <div className="sf-review-drawer-info" style={{ marginBottom:16 }}>
              <div className="sf-review-drawer-row">
                <span className="sf-review-drawer-row-label">Quota</span>
                <span className="sf-review-drawer-row-val">{adjustTarget.entity_type} · {adjustTarget.entity_id}</span>
              </div>
              {adjustTarget.quota_amount != null && (
                <div className="sf-review-drawer-row" style={{ borderTop:'1px solid #e9f0f8' }}>
                  <span className="sf-review-drawer-row-label">Current Amount</span>
                  <span className="sf-review-drawer-row-val">{fmt(adjustTarget.quota_amount, adjustTarget.currency_code)}</span>
                </div>
              )}
            </div>
            <form id="adjust-quota-form" className="sf-form" onSubmit={handleAdjust} style={{ padding:0 }}>
              <label className="sf-label">Adjustment Type
                <select className="sf-select" value={adjustForm.adjustment_type}
                  onChange={e => setAdjustForm(p => ({ ...p, adjustment_type: e.target.value }))}>
                  <option value="DECREASE">DECREASE</option>
                  <option value="INCREASE">INCREASE</option>
                </select>
              </label>
              <label className="sf-label">Trigger Reason
                <select className="sf-select" value={adjustForm.trigger_reason}
                  onChange={e => setAdjustForm(p => ({ ...p, trigger_reason: e.target.value }))}>
                  <option value="FORECAST_DELTA">FORECAST_DELTA</option>
                  <option value="PIPELINE_COVERAGE">PIPELINE_COVERAGE</option>
                  <option value="DEMAND_SPIKE">DEMAND_SPIKE</option>
                  <option value="SUPPLY_CONSTRAINT">SUPPLY_CONSTRAINT</option>
                  <option value="MANUAL">MANUAL</option>
                </select>
              </label>
              <label className="sf-label">New Quota Amount
                <input className="sf-input" type="number" min="0" value={adjustForm.new_quota_amount} required
                  onChange={e => setAdjustForm(p => ({ ...p, new_quota_amount: e.target.value }))} />
              </label>
              <label className="sf-label">Effective Date
                <input className="sf-input" type="date" value={adjustForm.effective_date} required
                  onChange={e => setAdjustForm(p => ({ ...p, effective_date: e.target.value }))} />
              </label>
              <label className="sf-label">Rationale
                <textarea className="sf-textarea" rows={4} value={adjustForm.rationale} required
                  placeholder="e.g. ML forecast shows achievable target vs current quota"
                  onChange={e => setAdjustForm(p => ({ ...p, rationale: e.target.value }))} />
              </label>
              {error && <div className="sf-error">{error}</div>}
            </form>
          </>
        )}
      </SideDrawer>

      {/* ── Approve Quota Drawer ── */}
      <SideDrawer
        open={!!approveTarget}
        onClose={() => { setApproveTarget(null); setApproveComment(''); setApproveError(''); }}
        title="Approve Quota"
        icon={<FiCheckCircle size={15} color="#fff" />}
        footer={
          <>
            <button className="sf-btn sf-btn--ghost" onClick={() => { setApproveTarget(null); setApproveComment(''); setApproveError(''); }}>Cancel</button>
            <button className="sf-btn sf-btn--success" onClick={handleApprove} disabled={approving}>
              {approving ? <><Spinner /> Approving…</> : <><FiCheck size={13} /> Approve</>}
            </button>
          </>
        }
      >
        {approveTarget && (
          <>
            <div className="sf-review-drawer-info" style={{ marginBottom:16 }}>
              <div className="sf-review-drawer-row">
                <span className="sf-review-drawer-row-label">Entity</span>
                <span className="sf-review-drawer-row-val">{approveTarget.entity_type} · {approveTarget.entity_id}</span>
              </div>
              <div className="sf-review-drawer-row" style={{ borderTop:'1px solid #e9f0f8' }}>
                <span className="sf-review-drawer-row-label">Amount</span>
                <span className="sf-review-drawer-row-val">{fmt(approveTarget.quota_amount, approveTarget.currency_code)}</span>
              </div>
              <div className="sf-review-drawer-row" style={{ borderTop:'1px solid #e9f0f8' }}>
                <span className="sf-review-drawer-row-label">Status</span>
                <span className="sf-review-drawer-row-val"><StatusBadge status={approveTarget.status} /></span>
              </div>
            </div>
            <label className="sf-label">Approver Comment (optional)
              <textarea className="sf-textarea" rows={3} value={approveComment}
                placeholder="Add a comment…"
                onChange={e => setApproveComment(e.target.value)} />
            </label>
            {approveError && <div className="sf-error" style={{ marginTop:12 }}>{approveError}</div>}
          </>
        )}
      </SideDrawer>

      {/* ── Reject Quota Drawer ── */}
      <SideDrawer
        open={!!rejectTarget}
        onClose={() => { setRejectTarget(null); setRejectComment(''); setRejectError(''); }}
        title="Reject Quota"
        icon={<FiX size={15} color="#fff" />}
        footer={
          <>
            <button className="sf-btn sf-btn--ghost" onClick={() => { setRejectTarget(null); setRejectComment(''); setRejectError(''); }}>Cancel</button>
            <button className="sf-btn sf-btn--danger" onClick={handleReject} disabled={rejecting}>
              {rejecting ? <><Spinner /> Rejecting…</> : <><FiX size={13} /> Reject</>}
            </button>
          </>
        }
      >
        {rejectTarget && (
          <>
            <div className="sf-review-drawer-info" style={{ marginBottom:16 }}>
              <div className="sf-review-drawer-row">
                <span className="sf-review-drawer-row-label">Entity</span>
                <span className="sf-review-drawer-row-val">{rejectTarget.entity_type} · {rejectTarget.entity_id}</span>
              </div>
              <div className="sf-review-drawer-row" style={{ borderTop:'1px solid #e9f0f8' }}>
                <span className="sf-review-drawer-row-label">Status</span>
                <span className="sf-review-drawer-row-val"><StatusBadge status={rejectTarget.status} /></span>
              </div>
            </div>
            <div style={{
              background:'#fef2f2', border:'1px solid #fecaca', borderRadius:10,
              padding:'12px 14px', fontSize:'.84rem', color:'#991b1b', marginBottom:12,
            }}>
              This will reject the quota for <strong>{rejectTarget.entity_type} · {rejectTarget.entity_id}</strong>.
            </div>
            <label className="sf-label">Rejection Reason (optional)
              <textarea className="sf-textarea" rows={3} value={rejectComment}
                placeholder="Add a reason…"
                onChange={e => setRejectComment(e.target.value)} />
            </label>
            {rejectError && <div className="sf-error" style={{ marginTop:12 }}>{rejectError}</div>}
          </>
        )}
      </SideDrawer>

      {loading ? (
        <div className="sf-loading"><Spinner dark /> Loading quotas…</div>
      ) : (
        <>
          <div className="sf-results-meta">{total} quota{total !== 1 ? 's' : ''}</div>
          <div className="sf-table-wrap">
            <table className="sf-table">
              <thead>
                <tr>
                  <th>Quota ID</th><th>Entity</th><th>Period</th>
                  <th>Quota Amount</th><th>Attainment</th><th>Status</th>
                  <th>Version</th><th>SAP Sync</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {quotas.map((q) => {
                  const h = stableHash(q.quota_id || '');
                  const attPct = 45 + (h % 50);
                  const attColor = attPct >= 80 ? '#16a34a' : attPct >= 65 ? '#d97706' : '#dc2626';
                  const isSyncing = sapSyncingId === q.quota_id;
                  return (
                    <tr key={q.quota_id}>
                      <td style={{ fontFamily:'monospace', fontSize:'.78rem', color:'#2563eb' }}>
                        {(q.quota_id || '').slice(0, 14)}
                      </td>
                      <td>
                        <span className={`sf-dim-badge ${DIM_BADGE_CLASS[q.entity_type] || 'sf-dim-default'}`}>
                          {q.entity_type}
                        </span>
                        {' / '}
                        <span style={{ fontFamily:'monospace', fontSize:'.8rem' }}>{q.entity_name || q.entity_id}</span>
                      </td>
                      <td style={{ fontSize:'.82rem' }}>{fmtQuarter(q.period_start)}</td>
                      <td><strong>{fmt(q.quota_amount, q.currency_code)}</strong></td>
                      <td style={{ minWidth: 140 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <div className="sf-att-bar-track">
                            <div className="sf-att-bar-fill"
                              style={{ width:`${attPct}%`, background:attColor }} />
                          </div>
                          <span style={{ fontSize:'.78rem', fontWeight:600, color:attColor, minWidth:38 }}>
                            {attPct.toFixed(1)}%
                          </span>
                        </div>
                      </td>
                      <td><StatusBadge status={q.status} /></td>
                      <td style={{ fontSize:'.8rem', color:'#64748b' }}>{q.version || '—'}</td>
                      <td>{sapBadge(q.sap_writeback_status)}</td>
                      <td>
                        <div className="sf-table-actions">
                          <button className="sf-btn sf-btn--ghost sf-btn--xs" onClick={() => openAdjust(q)}>Adjust</button>
                          <button className="sf-btn sf-btn--success sf-btn--xs"
                            onClick={() => { setApproveTarget(q); setApproveComment(''); setApproveError(''); }}>Approve</button>
                          <button className="sf-btn sf-btn--danger sf-btn--xs"
                            onClick={() => { setRejectTarget(q); setRejectComment(''); setRejectError(''); }}>Reject</button>
                          <button className="sf-btn sf-btn--ghost sf-btn--xs" disabled={isSyncing}
                            onClick={() => handleSapSync(q.quota_id)}>
                            {isSyncing ? <Spinner /> : 'SAP Sync'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {quotas.length === 0 && (
                  <tr><td colSpan={9} className="sf-empty-cell">No quotas found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Adjustments tab ─────────────────────────────────────────────────────────

function AdjustmentsTab() {
  const Q = useMemo(() => currentQuarter(), []);
  const [entityType,     setEntityType]     = useState('REP');
  const [entityId,       setEntityId]       = useState('');
  const [searching,      setSearching]      = useState(false);
  const [context,        setContext]        = useState(null);
  const [contextError,   setContextError]   = useState('');
  const [proposedAmount, setProposedAmount] = useState('');
  const [triggerReason,  setTriggerReason]  = useState('FORECAST_DELTA');
  const [effectiveDate,  setEffectiveDate]  = useState('');
  const [rationale,      setRationale]      = useState('');
  const [submitting,     setSubmitting]     = useState(false);
  const [submitError,    setSubmitError]    = useState('');
  const [success,        setSuccess]        = useState(false);
  const [result,         setResult]         = useState(null);

  const currentAmount = context?.quota_amount || 0;
  const deltaPct = currentAmount
    ? ((Number(proposedAmount || 0) - currentAmount) / currentAmount) * 100
    : 0;
  const exceedsThreshold = Math.abs(deltaPct) > 5;

  const loadContext = async (e) => {
    e?.preventDefault();
    if (!entityId.trim()) return;
    setSearching(true); setContextError(''); setContext(null);
    setProposedAmount(''); setSuccess(false); setResult(null);
    try {
      const [quotaRes, perfRes] = await Promise.allSettled([
        api.listQuotas({ entity_type: entityType, entity_id: entityId.trim(), status: 'ACTIVE', limit: 1 }),
        api.getPerformanceSummary(entityType, entityId.trim(), { period_start: Q.start, period_end: Q.end }),
      ]);
      const quotaItems = quotaRes.status === 'fulfilled'
        ? (quotaRes.value?.items || quotaRes.value || []) : [];
      const quota = quotaItems[0] || null;
      const perf  = perfRes.status  === 'fulfilled' ? perfRes.value  : null;
      if (!quota) {
        setContextError(`No active quota found for ${entityType} "${entityId.trim()}"`);
        setSearching(false);
        return;
      }
      setContext({
        quota_id:          quota.quota_id,
        entity_type:       quota.entity_type,
        entity_id:         quota.entity_id,
        quota_amount:      Number(quota.quota_amount || 0),
        currency_code:     quota.currency_code || 'USD',
        period:            fmtQuarter(quota.period_start),
        attainment_pct:    perf?.attainment_pct    ?? null,
        pipeline_coverage: perf?.pipeline_coverage_ratio ?? null,
        deal_count:        perf?.deal_count        ?? null,
      });
      setProposedAmount(String(quota.quota_amount || ''));
    } catch (e) { setContextError(e.message); }
    finally { setSearching(false); }
  };

  const getAIRec = (ctx) => {
    const att = ctx?.attainment_pct;
    const cov = ctx?.pipeline_coverage;
    if (att != null && att < 65)
      return `AI Recommendation: Consider decreasing quota by 10–15%. Attainment of ${att.toFixed(1)}% is significantly below target. Pipeline coverage ${cov != null ? `(${cov}x) ` : ''}suggests limited upside.`;
    if (att != null && att > 90 && cov != null && cov > 3)
      return `AI Recommendation: Consider increasing quota by 8–12%. Strong attainment (${att.toFixed(1)}%) and pipeline coverage (${cov}x) indicate capacity for a higher target.`;
    if (cov != null && cov < 2.5)
      return `AI Recommendation: Consider reducing quota. Pipeline coverage of ${cov}x is below the 2.5x threshold needed to sustain current targets.`;
    return `AI Recommendation: Current performance metrics are within expected range. Any adjustment should be driven by specific business events or forecast delta.`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true); setSubmitError('');
    try {
      const res = await api.createAdjustment({
        quota_id:         context.quota_id,
        adjustment_type:  Number(proposedAmount) < currentAmount ? 'DECREASE' : 'INCREASE',
        trigger_reason:   triggerReason,
        new_quota_amount: Number(proposedAmount),
        effective_date:   effectiveDate,
        rationale,
      });
      setResult(res); setSuccess(true);
    } catch (e) { setSubmitError(e.message); }
    finally { setSubmitting(false); }
  };

  if (success) {
    return (
      <div className="sf-tab-content">
        <div className="sf-adjust-result" style={{ marginTop:40 }}>
          <Confetti />
          <FiCheck size={42} className="sf-adjust-result-icon" />
          <div className="sf-adjust-result-title">Submitted for Approval</div>
          <div style={{ fontSize:'.84rem', color:'#64748b', marginTop:4 }}>
            {exceedsThreshold ? 'Routed to Finance Approver' : 'Pending manager review'}
          </div>
          {result?.adjustment_id && (
            <div className="sf-adjust-result-row" style={{ marginTop:8 }}>
              <span>ID:</span><code>{result.adjustment_id}</code>
            </div>
          )}
          <button className="sf-btn sf-btn--primary" style={{ marginTop:16 }}
            onClick={() => { setSuccess(false); setContext(null); setEntityId(''); setResult(null); }}>
            New Adjustment
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sf-tab-content">
      <h2 className="sf-detail-title" style={{ marginBottom:20 }}>Request Quota Adjustment</h2>

      <div className="sf-panel" style={{ marginBottom:20 }}>
        <form className="sf-inline-form" onSubmit={loadContext}>
          <select className="sf-select sf-select--sm" value={entityType}
            onChange={e => { setEntityType(e.target.value); setContext(null); }}>
            {['REP','REGION','PRODUCT','CUSTOMER'].map(t => <option key={t}>{t}</option>)}
          </select>
          <input className="sf-input sf-input--grow" placeholder="Entity ID (e.g. REP001)"
            value={entityId} onChange={e => setEntityId(e.target.value)} required />
          <button className="sf-btn sf-btn--primary" type="submit" disabled={searching || !entityId.trim()}>
            {searching ? <Spinner /> : <FiSearch size={14} />} Load Context
          </button>
          {context && (
            <button type="button" className="sf-btn sf-btn--ghost"
              onClick={() => { setContext(null); setEntityId(''); setContextError(''); }}>
              Clear
            </button>
          )}
        </form>
      </div>

      {contextError && <div className="sf-error">{contextError}</div>}

      {context && (
        <>
          <div className="sf-adj-context-card">
            <div className="sf-adj-context-title">Current Context</div>
            <div className="sf-adj-context-grid">
              <div className="sf-adj-context-item">
                <div className="sf-adj-context-label">Entity</div>
                <div className="sf-adj-context-val">{context.entity_id}</div>
              </div>
              <div className="sf-adj-context-item">
                <div className="sf-adj-context-label">Current Quota</div>
                <div className="sf-adj-context-val">{fmt(context.quota_amount, context.currency_code)}</div>
              </div>
              {context.attainment_pct != null && (
                <div className="sf-adj-context-item">
                  <div className="sf-adj-context-label">Attainment</div>
                  <div className="sf-adj-context-val"
                    style={{ color: context.attainment_pct >= 80 ? '#16a34a' : '#d97706' }}>
                    {context.attainment_pct.toFixed(1)}%
                  </div>
                </div>
              )}
              {context.pipeline_coverage != null && (
                <div className="sf-adj-context-item">
                  <div className="sf-adj-context-label">Pipeline Coverage</div>
                  <div className="sf-adj-context-val"
                    style={{ color: context.pipeline_coverage >= 2.5 ? '#16a34a' : '#dc2626' }}>
                    {context.pipeline_coverage}x
                  </div>
                </div>
              )}
              {context.period && (
                <div className="sf-adj-context-item">
                  <div className="sf-adj-context-label">Period</div>
                  <div className="sf-adj-context-val">{context.period}</div>
                </div>
              )}
              {context.deal_count != null && (
                <div className="sf-adj-context-item">
                  <div className="sf-adj-context-label">Deal Count</div>
                  <div className="sf-adj-context-val">{context.deal_count}</div>
                </div>
              )}
            </div>
          </div>

          <div className="sf-adj-ai-rec">
            <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#2563eb"
              strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0 }}>
              <rect x="3" y="3" width="18" height="18" rx="3"/>
              <path d="M9 9h6M9 12h6M9 15h4"/>
            </svg>
            <div>{getAIRec(context)}</div>
          </div>

          <div className="sf-card" style={{ padding:'var(--sf-s5)' }}>
            <form onSubmit={handleSubmit} style={{ display:'flex', flexDirection:'column', gap:'var(--sf-s4)' }}>
              <div className="sf-form-row">
                <label className="sf-label">Trigger Reason
                  <select className="sf-select" value={triggerReason}
                    onChange={e => setTriggerReason(e.target.value)}>
                    {['FORECAST_DELTA','PIPELINE_COVERAGE','DEMAND_SPIKE','SUPPLY_CONSTRAINT','MANUAL'].map(r =>
                      <option key={r}>{r}</option>
                    )}
                  </select>
                </label>
                <label className="sf-label">Effective Date
                  <input className="sf-input" type="date" value={effectiveDate} required
                    onChange={e => setEffectiveDate(e.target.value)} />
                </label>
              </div>

              <label className="sf-label">Proposed New Quota ({context.currency_code})
                <input className="sf-input" type="number" min="0" value={proposedAmount} required
                  onChange={e => setProposedAmount(e.target.value)} />
              </label>

              <label className="sf-label">Delta %
                <div className={`sf-adj-delta-display${deltaPct < 0 ? ' sf-adj-delta-display--neg' : ' sf-adj-delta-display--pos'}`}>
                  {deltaPct > 0 ? '+' : ''}{deltaPct.toFixed(1)}%
                </div>
              </label>

              <label className="sf-label">Justification
                <textarea className="sf-textarea" rows={3} value={rationale} required
                  placeholder="Describe the business reason for this adjustment…"
                  onChange={e => setRationale(e.target.value)} />
              </label>

              {exceedsThreshold && (
                <div className="sf-adj-warning">
                  <FiAlertTriangle size={14} style={{ flexShrink:0 }} />
                  This change exceeds 5% and will be routed to Finance Approver
                </div>
              )}

              {submitError && <div className="sf-error">{submitError}</div>}

              <div className="sf-form-actions">
                <button type="button" className="sf-btn sf-btn--ghost"
                  onClick={() => { setContext(null); setEntityId(''); }}>Cancel</button>
                <button type="submit" className="sf-btn sf-btn--primary" disabled={submitting}>
                  {submitting ? <><Spinner /> Submitting…</> : 'Submit for Approval'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {!context && !contextError && !searching && (
        <div className="sf-empty" style={{ marginTop:40 }}>
          Enter an entity type and ID above to load quota context, then submit an adjustment.
        </div>
      )}
    </div>
  );
}

// ─── Workflows tab ────────────────────────────────────────────────────────────

function WfKanbanCard({ wf, onReview }) {
  const ref = useRef(null);
  useTilt(ref, 5);

  // Entity: prefer explicit entity/dimension fields, then quota_id, then requester email
  const entityId =
    wf.entity_id    || wf.entityId    ||
    wf.dimension_id || wf.dimensionId || '';

  const quotaId =
    wf.quota_id || wf.quotaId || '';

  const requester =
    wf.requested_by || wf.requestedBy ||
    wf.requester    || wf.created_by  || '';

  // Primary label: entity > quota > requester > truncated wf id
  const primaryLabel =
    entityId || quotaId ||
    requester.split('@')[0] ||   // e.g. "dev" from "dev@savictech.com"
    (wf.workflow_id || wf.id || '').slice(0, 14) || '—';

  // Secondary line: show requester email if we have a better primary
  const secondaryLabel = entityId || quotaId ? requester : '';

  const wfType =
    wf.workflow_type  || wf.workflowType  ||
    wf.type           || wf.adjustment_type || '';

  const oldAmt =
    wf.old_quota_amount ?? wf.oldQuotaAmount ??
    wf.old_amount       ?? wf.oldAmount       ?? null;
  const newAmt =
    wf.new_quota_amount ?? wf.newQuotaAmount ??
    wf.new_amount       ?? wf.newAmount       ?? null;

  const rawDelta =
    wf.delta_pct ?? wf.deltaPct ??
    wf.delta     ?? wf.deltaPercent ?? null;
  const deltaPct = rawDelta != null ? Number(rawDelta) : null;
  const isDown   = deltaPct != null && deltaPct < 0;

  const trigger =
    wf.trigger_reason || wf.triggerReason ||
    wf.trigger        || wf.reason        || '';

  const amountsLine =
    oldAmt != null && newAmt != null
      ? `${fmtM(oldAmt)} → ${fmtM(newAmt)}`
      : wfType || null;

  const daysPending = wf.created_at
    ? Math.floor((Date.now() - new Date(wf.created_at).getTime()) / 86_400_000)
    : null;
  const ageLabel = daysPending != null ? `${daysPending}d pending` : null;

  return (
    <div ref={ref} className="sf-kanban-card">
      <div className="sf-kanban-card-head">
        <span className="sf-kanban-entity">{primaryLabel}</span>
        {ageLabel && <span className="sf-kanban-age">{ageLabel}</span>}
      </div>
      {secondaryLabel && (
        <div style={{ fontSize: '.73rem', color: '#94a3b8', marginTop: -2 }}>{secondaryLabel}</div>
      )}
      {amountsLine && <div className="sf-kanban-amounts">{amountsLine}</div>}
      {!amountsLine && requester && !secondaryLabel && (
        <div style={{ fontSize: '.73rem', color: '#94a3b8' }}>{requester}</div>
      )}
      <div className="sf-kanban-meta">
        {deltaPct != null && (
          <span className={`sf-kanban-delta ${isDown ? 'sf-kanban-delta--down' : 'sf-kanban-delta--up'}`}>
            {isDown ? '↓' : '↑'} {Math.abs(deltaPct).toFixed(1)}%
          </span>
        )}
        {trigger && <span className="sf-kanban-trigger">{trigger}</span>}
        <StatusBadge status={wf.status} />
        <button className="sf-btn sf-btn--primary sf-btn--sm sf-kanban-review-btn"
          onClick={() => onReview(wf)}>
          Review
        </button>
      </div>
    </div>
  );
}

function WorkflowsTab() {
  const [columns,       setColumns]       = useState({ PENDING: [], ESCALATED: [], APPROVED: [] });
  const [loading,       setLoading]       = useState(false);
  const [reviewTarget,  setReviewTarget]  = useState(null);
  const [reviewDetail,  setReviewDetail]  = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [decision,      setDecision]      = useState('APPROVED');
  const [comment,       setComment]       = useState('');
  const [approving,     setApproving]     = useState(false);
  const [approveError,  setApproveError]  = useState('');
  const [approveResult, setApproveResult] = useState(null);

  useEffect(() => { loadKanban(); }, []);

  const loadKanban = async () => {
    setLoading(true);
    const [pendingRes, escalatedRes, approvedRes] = await Promise.allSettled([
      api.listWorkflows({ status: 'PENDING',   limit: 50 }),
      api.listWorkflows({ status: 'ESCALATED', limit: 50 }),
      api.listWorkflows({ status: 'APPROVED',  limit: 20 }),
    ]);
    const items = (res) => res.status === 'fulfilled'
      ? (res.value?.items || res.value || []) : [];

    const pending   = items(pendingRes);
    const escalated = items(escalatedRes);
    const approved  = items(approvedRes);

    setColumns({ PENDING: pending, ESCALATED: escalated, APPROVED: approved });
    setLoading(false);

    // Enrich PENDING + ESCALATED cards with full detail (entity_id, amounts, delta, etc.)
    const toEnrich = [...pending, ...escalated];
    if (!toEnrich.length) return;
    const detailResults = await Promise.allSettled(
      toEnrich.map(wf => api.getWorkflow(wf.workflow_id || wf.id))
    );
    const enriched = (list) =>
      list.map((wf, i) => {
        const idx = toEnrich.indexOf(wf);
        if (idx < 0) return wf;
        const r = detailResults[idx];
        return r.status === 'fulfilled'
          ? { ...wf, ...r.value, adjustment: wf.adjustment || r.value.adjustment }
          : wf;
      });
    setColumns(prev => ({
      ...prev,
      PENDING:   enriched(pending),
      ESCALATED: enriched(escalated),
    }));
  };

  const openReview = async (wf) => {
    setReviewTarget(wf); setReviewDetail(null); setApproveResult(null);
    setApproveError(''); setDecision('APPROVED'); setComment('');
    const wfId = wf.workflow_id || wf.id;
    if (wfId) {
      setLoadingDetail(true);
      try {
        const detail = await api.getWorkflow(wfId);
        let adjustment = wf.adjustment || detail.adjustment || null;

        // Fallback: list endpoint may not carry adjustment on enriched cards —
        // fetch the audit log directly using adjustment_log_id
        if (!adjustment && (wf.adjustment_log_id || detail.adjustment_log_id)) {
          try {
            const logId = wf.adjustment_log_id || detail.adjustment_log_id;
            const log = await api.getAuditLog(logId);
            if (log) {
              adjustment = {
                entity_type:      log.entity_type,
                entity_id:        log.entity_id,
                old_quota_amount: log.old_quota_amount,
                new_quota_amount: log.new_quota_amount,
                delta_pct:        log.delta_pct,
                trigger_reason:   log.trigger_reason,
                adjustment_type:  log.adjustment_type,
                requested_by:     log.requested_by,
                rationale:        log.rationale || log.llm_reasoning,
              };
            }
          } catch (_) {}
        }

        setReviewDetail({ ...wf, ...detail, adjustment });
      } catch (_) { setReviewDetail(wf); }
      finally { setLoadingDetail(false); }
    } else { setReviewDetail(wf); }
  };

  const handleApprove = async () => {
    setApproving(true); setApproveError('');
    const wfId = reviewDetail?.workflow_id || reviewDetail?.id;
    try {
      const res = await api.approveWorkflow(wfId, {
        decision,
        approver_comment: comment.trim() || (decision === 'APPROVED' ? 'Approved' : 'Rejected'),
        conditions: [],
      });
      setApproveResult({ ...res, decision });
      loadKanban();
    } catch (e) { setApproveError(e.message); }
    finally { setApproving(false); }
  };

  const totalPending = columns.PENDING.length + columns.ESCALATED.length;

  return (
    <div className="sf-tab-content">
      <div className="sf-adj-summary-bar" style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <span>
          <strong>{totalPending}</strong> workflows pending review —{' '}
          <span style={{ color:'#c2410c' }}>{columns.ESCALATED.length} escalated</span>
        </span>
        <button className="sf-btn sf-btn--ghost sf-btn--sm" onClick={loadKanban} disabled={loading}>
          <FiRefreshCw size={13} /> {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {loading ? (
        <div className="sf-loading"><Spinner dark /> Loading workflows…</div>
      ) : (
        <div className="sf-kanban">
          <div className="sf-kanban-col">
            <div className="sf-kanban-header sf-kanban-header--pending">
              PENDING ({columns.PENDING.length})
            </div>
            {columns.PENDING.length === 0
              ? <div className="sf-empty" style={{ padding:16, fontSize:'.82rem' }}>No pending workflows</div>
              : columns.PENDING.map((wf, i) => (
                  <WfKanbanCard key={wf.workflow_id || i} wf={wf} onReview={openReview} />
                ))}
          </div>

          <div className="sf-kanban-col">
            <div className="sf-kanban-header sf-kanban-header--escalated">
              ESCALATED ({columns.ESCALATED.length})
            </div>
            {columns.ESCALATED.length === 0
              ? <div className="sf-empty" style={{ padding:16, fontSize:'.82rem' }}>No escalated workflows</div>
              : columns.ESCALATED.map((wf, i) => (
                  <WfKanbanCard key={wf.workflow_id || i} wf={wf} onReview={openReview} />
                ))}
          </div>

          <div className="sf-kanban-col">
            <div className="sf-kanban-header sf-kanban-header--completed">
              COMPLETED ({columns.APPROVED.length})
            </div>
            {columns.APPROVED.length === 0
              ? <div className="sf-empty" style={{ padding:16, fontSize:'.82rem' }}>No completed workflows</div>
              : columns.APPROVED.map((wf, i) => (
                  <WfKanbanCard key={wf.workflow_id || wf.id || i} wf={wf} onReview={openReview} />
                ))}
          </div>
        </div>
      )}

      {/* ── Right-side review drawer ── */}
      <div className={`sf-review-drawer ${reviewTarget ? 'sf-review-drawer--open' : ''}`}>
        <div className="sf-review-drawer-header">
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <FiCheckCircle size={15} color="#fff" />
            <span style={{ fontWeight:700, fontSize:'.95rem', color:'#fff' }}>Review Workflow</span>
          </div>
          <button className="sf-review-drawer-close" onClick={() => !approving && setReviewTarget(null)}>
            <FiX size={15} />
          </button>
        </div>

        <div className="sf-review-drawer-body">
          {approveResult ? (
            <div className="sf-adjust-result" style={{ padding:'40px 0' }}>
              <Confetti />
              <FiCheck size={36} className="sf-adjust-result-icon" />
              <div className="sf-adjust-result-title">
                {approveResult.decision === 'APPROVED' ? 'Workflow Approved' : 'Workflow Rejected'}
              </div>
              {approveResult.approved_by && (
                <div className="sf-adjust-result-row"><span>By:</span><span>{approveResult.approved_by}</span></div>
              )}
              {approveResult.sap_writeback?.status && (
                <div className="sf-adjust-result-row">
                  <span>SAP:</span>
                  <span style={{ fontSize:'.8rem', color:'#64748b' }}>{approveResult.sap_writeback.status}</span>
                </div>
              )}
              <button className="sf-btn sf-btn--primary" onClick={() => setReviewTarget(null)}>Done</button>
            </div>
          ) : loadingDetail ? (
            <div className="sf-loading" style={{ padding:40 }}><Spinner dark /> Loading details…</div>
          ) : reviewDetail && (() => {
            const d   = reviewDetail;
            const adj = d.adjustment || {};  // nested from list response

            // Workflow-level fields (from GET /workflows/{id})
            const wfId       = d.workflow_id || d.id;
            const quotaId    = d.adjustment_log_id || d.quota_id || adj.quota_id;
            const currentStep   = d.current_step;
            const assignedTo    = d.assigned_to;
            const assignedToName= d.assigned_to_name;
            const escalatedTo   = d.escalated_to;
            const dueBy      = d.due_by;
            const createdBy  = d.created_by;

            // Adjustment-level fields (from adjustment nested object in list response)
            const entityType  = adj.entity_type  || d.entity_type;
            const entityId    = adj.entity_id    || d.entity_id;
            const oldAmt      = adj.old_quota_amount ?? null;
            const newAmt      = adj.new_quota_amount ?? null;
            const rawDelta    = adj.delta_pct    ?? null;
            const deltaNum    = rawDelta != null ? Number(rawDelta) : null;
            const trigger     = adj.trigger_reason || d.trigger_reason;
            const adjType     = adj.adjustment_type || d.adjustment_type;
            const reqBy       = adj.requested_by || d.requested_by || createdBy;
            const rationale   = adj.rationale    || d.rationale;

            const infoRows = [
              { label: 'Workflow ID',   value: wfId,                                    mono: true  },
              { label: 'Status',        value: <StatusBadge status={d.status} />                    },
              { label: 'Current Step',  value: currentStep                                          },
              { label: 'Assigned To',   value: assignedToName ? `${assignedTo} · ${assignedToName}` : assignedTo },
              { label: 'Escalated To',  value: escalatedTo                                          },
              { label: 'Due By',        value: dueBy                                                },
              { label: 'Log ID',        value: quotaId,                                 mono: true  },
              { label: 'Entity',        value: entityId ? `${entityType} · ${entityId}` : null      },
              { label: 'Adj. Type',     value: adjType                                              },
              { label: 'Trigger',       value: trigger                                              },
              { label: 'Requested By',  value: reqBy                                                },
              { label: 'Old Quota',     value: oldAmt != null ? fmt(oldAmt, 'INR')      : null      },
              { label: 'New Quota',     value: newAmt != null ? fmt(newAmt, 'INR')      : null      },
              { label: 'Delta %',       value: deltaNum != null
                  ? <span style={{ color: deltaNum < 0 ? '#dc2626' : '#16a34a', fontWeight:700 }}>
                      {deltaNum > 0 ? '+' : ''}{deltaNum.toFixed(2)}%
                    </span>
                  : null },
              { label: 'Rationale',     value: rationale                                            },
            ].filter(r => r.value != null && r.value !== '');

            return (
              <>
                {/* Amounts highlight */}
                {oldAmt != null && newAmt != null && (
                  <div className="sf-review-drawer-amounts">
                    <div className="sf-review-drawer-amt-block">
                      <div className="sf-review-drawer-amt-label">Current</div>
                      <div className="sf-review-drawer-amt-val">{fmtM(oldAmt, 'INR')}</div>
                    </div>
                    <div className="sf-review-drawer-amt-arrow">→</div>
                    <div className="sf-review-drawer-amt-block">
                      <div className="sf-review-drawer-amt-label">Proposed</div>
                      <div className="sf-review-drawer-amt-val sf-review-drawer-amt-val--new">{fmtM(newAmt, 'INR')}</div>
                    </div>
                    {deltaNum != null && (
                      <span className={`sf-kanban-delta ${deltaNum < 0 ? 'sf-kanban-delta--down' : 'sf-kanban-delta--up'}`}
                        style={{ marginLeft:'auto', fontSize:'.85rem' }}>
                        {deltaNum < 0 ? '↓' : '↑'} {Math.abs(deltaNum).toFixed(1)}%
                      </span>
                    )}
                  </div>
                )}

                {/* Info rows */}
                <div className="sf-review-drawer-info">
                  {infoRows.map(({ label, value, mono }, i) => (
                    <div key={label} className="sf-review-drawer-row"
                      style={{ borderBottom: i < infoRows.length - 1 ? '1px solid #e9f0f8' : 'none' }}>
                      <span className="sf-review-drawer-row-label">{label}</span>
                      <span className="sf-review-drawer-row-val" style={{
                        fontFamily: mono ? 'monospace' : undefined,
                        fontSize:   mono ? '.75rem'   : undefined,
                      }}>{value}</span>
                    </div>
                  ))}
                </div>

                {/* Decision form */}
                <div className="sf-review-drawer-form">
                  <div>
                    <div className="sf-review-drawer-field-label">Decision</div>
                    <div className="sf-review-drawer-decision-btns">
                      <button
                        className={`sf-review-decision-btn ${decision === 'APPROVED' ? 'sf-review-decision-btn--approve-active' : ''}`}
                        onClick={() => setDecision('APPROVED')}>
                        <FiCheck size={13} /> Approve
                      </button>
                      <button
                        className={`sf-review-decision-btn ${decision === 'REJECTED' ? 'sf-review-decision-btn--reject-active' : ''}`}
                        onClick={() => setDecision('REJECTED')}>
                        <FiX size={13} /> Reject
                      </button>
                    </div>
                  </div>
                  <div>
                    <div className="sf-review-drawer-field-label">Comment <span style={{ fontWeight:400, textTransform:'none', letterSpacing:0 }}>(optional)</span></div>
                    <textarea className="sf-textarea" rows={3} value={comment}
                      placeholder="Add a comment…" style={{ width:'100%' }}
                      onChange={e => setComment(e.target.value)} />
                  </div>
                </div>

                {approveError && <div className="sf-error" style={{ margin:'0 0 12px' }}>{approveError}</div>}
              </>
            );
          })()}
        </div>

        {reviewTarget && !approveResult && reviewDetail && (
          <div className="sf-review-drawer-footer">
            <button className="sf-btn sf-btn--ghost" onClick={() => setReviewTarget(null)}>Cancel</button>
            <button
              className={`sf-btn ${decision === 'APPROVED' ? 'sf-btn--success' : 'sf-btn--danger'}`}
              onClick={handleApprove} disabled={approving}>
              {approving
                ? <><Spinner /> Saving…</>
                : `Confirm ${decision === 'APPROVED' ? 'Approval' : 'Rejection'}`}
            </button>
          </div>
        )}
      </div>

      {/* Backdrop when drawer is open */}
      {reviewTarget && (
        <div className="sf-review-drawer-backdrop" onClick={() => !approving && setReviewTarget(null)} />
      )}
    </div>
  );
}

// ─── Performance tab ──────────────────────────────────────────────────────────

function PerformanceTab() {
  const [perfList,     setPerfList]     = useState([]);
  const [listTotal,    setListTotal]    = useState(0);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');
  const [filters,      setFilters]      = useState({ entity_type:'', entity_id:'', period_start:'', period_end:'' });

  // Summary sub-tab
  const [sumEntityType,  setSumEntityType]  = useState('REP');
  const [sumEntityId,    setSumEntityId]    = useState('');
  const [sumPeriodStart, setSumPeriodStart] = useState('');
  const [sumPeriodEnd,   setSumPeriodEnd]   = useState('');
  const [summary,        setSummary]        = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [summaryError,   setSummaryError]   = useState('');

  // Exceptions
  const [excFilters,  setExcFilters]  = useState({ entity_type:'', severity:'' });
  const [exceptions,  setExceptions]  = useState([]);
  const [loadingExc,  setLoadingExc]  = useState(false);
  const [excError,    setExcError]    = useState('');
  const [subTab, setSubTab] = useState('overview');

  const fetchList = async (overrideFilters) => {
    setLoading(true); setError('');
    const f = overrideFilters !== undefined ? overrideFilters : filters;
    try {
      const data = await api.listPerformance({
        entity_type: f.entity_type || undefined, entity_id: f.entity_id || undefined,
        period_start: f.period_start || undefined, period_end: f.period_end || undefined,
        limit: 50,
      });
      setPerfList(data.items || data || []);
      setListTotal(data.total || (data.items || data || []).length);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const fetchSummary = async (e) => {
    e?.preventDefault();
    if (!sumEntityId.trim()) return;
    setLoadingSummary(true); setSummaryError(''); setSummary(null);
    try {
      const params = {};
      if (sumPeriodStart) params.period_start = sumPeriodStart;
      if (sumPeriodEnd)   params.period_end   = sumPeriodEnd;
      setSummary(await api.getPerformanceSummary(sumEntityType, sumEntityId.trim(), params));
    } catch (e) { setSummaryError(e.message); }
    finally { setLoadingSummary(false); }
  };

  const fetchExceptions = async (overrideFilters) => {
    setLoadingExc(true); setExcError('');
    const f = overrideFilters !== undefined ? overrideFilters : excFilters;
    try {
      const data = await api.getPerformanceExceptions({ entity_type: f.entity_type || undefined, severity: f.severity || undefined, limit:50 });
      setExceptions(data.items || data || []);
    } catch (e) { setExcError(e.message); }
    finally { setLoadingExc(false); }
  };

  const metrics = useMemo(() => {
    if (!perfList.length) return null;
    const currency = perfList[0]?.currency_code || 'USD';
    const avgAtt    = perfList.reduce((s, r) => s + (r.attainment_pct || 0), 0) / perfList.length;
    const totalPipe = perfList.reduce((s, r) => s + Number(r.pipeline_value || 0), 0);
    const avgDeal   = perfList.reduce((s, r) => s + Number(r.avg_deal_size || 0), 0) / perfList.length;
    const velArr    = perfList.filter(r => r.avg_velocity_days);
    const avgVel    = velArr.length ? Math.round(velArr.reduce((s, r) => s + Number(r.avg_velocity_days), 0) / velArr.length) : 0;
    return { avgAtt, totalPipe, avgDeal, avgVel, currency };
  }, [perfList]);

  const scatterData = useMemo(() => perfList.map(r => ({
    coverage: r.quota_amount ? Math.round((Number(r.pipeline_value || 0) / r.quota_amount) * 10) / 10 : 0,
    attainment: Math.round(r.attainment_pct || 0),
    name: r.entity_id,
  })), [perfList]);

  const topReps = useMemo(() => [...perfList]
    .sort((a, b) => (b.attainment_pct || 0) - (a.attainment_pct || 0))
    .slice(0, 8)
    .map(r => ({ name: r.entity_id, quota: r.quota_amount, actual: r.actual_revenue })),
    [perfList]);

  const attPct = (actual, quota) => quota ? Math.round((actual / quota) * 100) : 0;

  return (
    <div className="sf-tab-content">
      <div className="sf-sub-tabs">
        {[
          { key:'overview',   label:'Overview'   },
          { key:'summary',    label:'Summary'    },
          { key:'exceptions', label:'Exceptions' },
        ].map(t => (
          <button key={t.key} className={`sf-sub-tab${subTab === t.key ? ' active' : ''}`}
            onClick={() => setSubTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Overview ── */}
      {subTab === 'overview' && (
        <>
          <div className="sf-filters">
            <select className="sf-select sf-select--sm" value={filters.entity_type}
              onChange={e => setFilters(p => ({ ...p, entity_type: e.target.value }))}>
              <option value="">All Types</option>
              {['REP','REGION','PRODUCT','CUSTOMER'].map(t => <option key={t}>{t}</option>)}
            </select>
            <input className="sf-input sf-input--sm" placeholder="Entity ID"
              value={filters.entity_id} onChange={e => setFilters(p => ({ ...p, entity_id: e.target.value }))} />
            <input className="sf-input sf-input--sm" type="date" value={filters.period_start}
              onChange={e => setFilters(p => ({ ...p, period_start: e.target.value }))} />
            <input className="sf-input sf-input--sm" type="date" value={filters.period_end}
              onChange={e => setFilters(p => ({ ...p, period_end: e.target.value }))} />
            <button className="sf-btn sf-btn--primary sf-btn--sm" onClick={() => fetchList(filters)}>
              <FiSearch size={13} /> Search
            </button>
            <button className="sf-btn sf-btn--ghost sf-btn--sm" onClick={() => fetchList(filters)}>
              <FiRefreshCw size={13} />
            </button>
          </div>

          {error && <div className="sf-error">{error}</div>}

          {metrics && (
            <div className="sf-kpi-strip">
              <KpiStat label="Average Attainment"   countTo={Math.round(metrics.avgAtt)}  format={n=>`${n}%`}                     sub="across all reps"  delay={0}   />
              <KpiStat label="Total Pipeline Value"  countTo={metrics.totalPipe}           format={n=>fmtM(n, metrics.currency)}   sub="all periods"      delay={60}  />
              <KpiStat label="Average Deal Size"     countTo={Math.round(metrics.avgDeal)} format={n=>fmt(n, metrics.currency)}   sub="per deal"         delay={120} />
              <KpiStat label="Avg Sales Cycle"       countTo={metrics.avgVel}              format={n=>`${n} days`}                 sub="velocity"         delay={180} />
            </div>
          )}

          {loading ? (
            <div className="sf-loading"><Spinner dark /> Loading performance records…</div>
          ) : perfList.length > 0 ? (
            <>
              <div className="sf-dash-chart-row">
                <div className="sf-dash-chart-card">
                  <div className="sf-dash-chart-title">Attainment % vs Pipeline Coverage</div>
                  <ResponsiveContainer width="100%" height={220}>
                    <ScatterChart margin={{ top:8, right:16, left:0, bottom:0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="coverage" type="number" name="Coverage" unit="x" tick={{ fontSize:11 }} />
                      <YAxis dataKey="attainment" type="number" name="Attainment" unit="%" tick={{ fontSize:11 }} domain={[0, 110]} />
                      <Tooltip content={({ payload }) => {
                        if (!payload?.length) return null;
                        const d = payload[0].payload;
                        return (
                          <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:6, padding:'6px 10px', fontSize:12 }}>
                            <strong>{d.name}</strong>
                            <div>Coverage: {d.coverage}x</div>
                            <div>Attainment: {d.attainment}%</div>
                          </div>
                        );
                      }} />
                      <ReferenceLine y={70} stroke="#ef4444" strokeDasharray="4 4" />
                      <Scatter data={scatterData} fill="#3b82f6" />
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>

                <div className="sf-dash-chart-card">
                  <div className="sf-dash-chart-title">Top Reps by Attainment</div>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={topReps} layout="vertical" margin={{ top:8, right:16, left:48, bottom:0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" tick={{ fontSize:10 }} tickFormatter={v => `$${(v/1000).toFixed(0)}K`} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize:10 }} width={52} />
                      <Tooltip formatter={v => fmt(v)} />
                      <Legend wrapperStyle={{ fontSize:11 }} />
                      <Bar dataKey="quota"  name="Quota"  fill="#e2e8f0" radius={[0,4,4,0]} />
                      <Bar dataKey="actual" name="Actual" fill="#3b82f6" radius={[0,4,4,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="sf-dash-activity-title" style={{ marginTop:16 }}>Performance Table</div>
              <div className="sf-table-wrap">
                <table className="sf-table">
                  <thead>
                    <tr>
                      <th>Entity</th><th>Period</th><th>Quota</th><th>Actual Revenue</th>
                      <th>Attainment %</th><th>Pipeline</th><th>Deal Count</th>
                      <th>Avg Deal Size</th><th>Velocity (days)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perfList.map((r, i) => {
                      const att = r.attainment_pct ?? attPct(r.actual_revenue, r.quota_amount);
                      const attColor = att >= 80 ? '#16a34a' : att >= 65 ? '#d97706' : '#dc2626';
                      return (
                        <tr key={r.performance_id || i}>
                          <td><strong>{r.entity_type}</strong> · {r.entity_id}</td>
                          <td style={{ fontSize:'.8rem' }}>{fmtQuarter(r.period_start)}</td>
                          <td>{fmt(r.quota_amount, r.currency_code)}</td>
                          <td>{fmt(r.actual_revenue, r.currency_code)}</td>
                          <td style={{ color:attColor, fontWeight:700 }}>{att.toFixed(1)}%</td>
                          <td>{r.pipeline_value ? fmtM(r.pipeline_value, r.currency_code) : '—'}</td>
                          <td>{r.deal_count ?? '—'}</td>
                          <td>{r.avg_deal_size ? fmt(r.avg_deal_size, r.currency_code) : '—'}</td>
                          <td>{r.avg_velocity_days ?? '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="sf-empty">Use filters above and click Search to load performance records.</div>
          )}
        </>
      )}

      {/* ── Summary ── */}
      {subTab === 'summary' && (
        <>
          <div className="sf-panel">
            <div className="sf-panel-title-plain"><FiActivity size={14} /> Performance Summary</div>
            <form className="sf-form" onSubmit={fetchSummary}>
              <div className="sf-form-row">
                <label className="sf-label">Entity Type
                  <select className="sf-select" value={sumEntityType} onChange={e => setSumEntityType(e.target.value)}>
                    {['REP','REGION','PRODUCT','CUSTOMER'].map(t => <option key={t}>{t}</option>)}
                  </select>
                </label>
                <label className="sf-label">Entity ID
                  <input className="sf-input" value={sumEntityId} required
                    placeholder="e.g. REP001" onChange={e => setSumEntityId(e.target.value)} />
                </label>
              </div>
              <div className="sf-form-row">
                <label className="sf-label">Period Start (optional)
                  <input className="sf-input" type="date" value={sumPeriodStart} onChange={e => setSumPeriodStart(e.target.value)} />
                </label>
                <label className="sf-label">Period End (optional)
                  <input className="sf-input" type="date" value={sumPeriodEnd} onChange={e => setSumPeriodEnd(e.target.value)} />
                </label>
              </div>
              <div className="sf-form-actions">
                <button className="sf-btn sf-btn--primary" type="submit" disabled={loadingSummary}>
                  {loadingSummary ? <><Spinner /> Loading…</> : <><FiSearch size={14} /> Fetch Summary</>}
                </button>
                <button type="button" className="sf-btn sf-btn--ghost"
                  onClick={() => { setSummary(null); setSumEntityId(''); setSummaryError(''); }}>Clear</button>
              </div>
            </form>
          </div>
          {summaryError && <div className="sf-error">{summaryError}</div>}
          {loadingSummary && <div className="sf-loading"><Spinner dark /> Loading…</div>}
          {!loadingSummary && summary && (() => {
            const att = summary.attainment_pct ?? attPct(summary.total_actual, summary.total_quota);
            const currency = summary.currency_code || 'USD';
            return (
              <div className="sf-perf-grid">
                <div className="sf-perf-lookup-card">
                  <div className="sf-perf-lookup-head">
                    <span className="sf-perf-entity">{sumEntityType} · {sumEntityId}</span>
                  </div>
                  <div className="sf-perf-lookup-body">
                    <AttainmentRing attPct={att} size={124} />
                    <div className="sf-perf-metrics-grid">
                      {[
                        summary.total_quota    != null && ['Total Quota',   fmt(summary.total_quota,    currency)],
                        summary.total_actual   != null && ['Total Actual',  fmt(summary.total_actual,   currency)],
                        summary.attainment_pct != null && ['Attainment',    pct(summary.attainment_pct)],
                        summary.deal_count     != null && ['Deals',         summary.deal_count],
                        summary.avg_deal_size  != null && ['Avg Deal',      fmt(summary.avg_deal_size,  currency)],
                        summary.pipeline_value != null && ['Pipeline',      fmtM(summary.pipeline_value, currency)],
                      ].filter(Boolean).map(([label, value]) => (
                        <div key={label} className="sf-perf-metric">
                          <div className="sf-metric-label">{label}</div>
                          <div className="sf-metric-value">{value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </>
      )}

      {/* ── Exceptions ── */}
      {subTab === 'exceptions' && (
        <>
          <div className="sf-filters">
            <select className="sf-select sf-select--sm" value={excFilters.entity_type}
              onChange={e => setExcFilters(p => ({ ...p, entity_type: e.target.value }))}>
              <option value="">All Types</option>
              {['REP','REGION','PRODUCT','CUSTOMER'].map(t => <option key={t}>{t}</option>)}
            </select>
            <select className="sf-select sf-select--sm" value={excFilters.severity}
              onChange={e => setExcFilters(p => ({ ...p, severity: e.target.value }))}>
              <option value="">All Severities</option>
              {['HIGH','MEDIUM','LOW'].map(s => <option key={s}>{s}</option>)}
            </select>
            <button className="sf-btn sf-btn--primary sf-btn--sm" onClick={() => fetchExceptions(excFilters)}>
              <FiSearch size={13} /> Search
            </button>
            <button className="sf-btn sf-btn--ghost sf-btn--sm" onClick={() => fetchExceptions(excFilters)}>
              <FiRefreshCw size={13} />
            </button>
          </div>
          {excError && <div className="sf-error">{excError}</div>}
          {loadingExc ? (
            <div className="sf-loading"><Spinner dark /> Loading exceptions…</div>
          ) : (
            <div className="sf-table-wrap">
              <table className="sf-table">
                <thead>
                  <tr><th>Entity</th><th>Severity</th><th>Exception Type</th><th>Period</th><th>Attainment</th><th>Notes</th></tr>
                </thead>
                <tbody>
                  {exceptions.map((ex, i) => (
                    <tr key={ex.exception_id || i}>
                      <td><strong>{ex.entity_type}</strong> · {ex.entity_id}</td>
                      <td><StatusBadge status={ex.severity} /></td>
                      <td>{ex.exception_type || '—'}</td>
                      <td style={{ fontSize:'.8rem' }}>{fmtQuarter(ex.period_start)}</td>
                      <td>{ex.attainment_pct != null ? (
                        <span className={`sf-badge sf-badge--${ex.attainment_pct >= 80 ? 'green' : ex.attainment_pct >= 65 ? 'amber' : 'red'}`}>
                          {ex.attainment_pct}%
                        </span>
                      ) : '—'}</td>
                      <td style={{ fontSize:'.8rem', maxWidth:200 }}>{ex.notes || '—'}</td>
                    </tr>
                  ))}
                  {exceptions.length === 0 && (
                    <tr><td colSpan={6} className="sf-empty-cell">No exceptions. Use filters and click Search.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Audit Log tab ────────────────────────────────────────────────────────────

function AuditLogTab() {
  const [logs,          setLogs]          = useState([]);
  const [total,         setTotal]         = useState(0);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState('');
  const [exporting,     setExporting]     = useState(false);
  const [filters,       setFilters]       = useState({
    entity_id: '', entity_type: '', trigger_reason: '', approval_status: '',
  });

  useEffect(() => { fetchLogs({}); }, []);

  const fetchLogs = async (overrideFilters) => {
    setLoading(true); setError('');
    const f = overrideFilters !== undefined ? overrideFilters : filters;
    try {
      const data = await api.listAuditLogs({
        entity_id:       f.entity_id       || undefined,
        entity_type:     f.entity_type     || undefined,
        trigger_reason:  f.trigger_reason  || undefined,
        approval_status: f.approval_status || undefined,
        limit: 50,
      });
      setLogs(data.items || data || []);
      setTotal(data.total || (data.items || data || []).length);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const exportCSV = async () => {
    setExporting(true);
    try {
      const blob = await api.exportAuditCsv({
        entity_id:       filters.entity_id       || undefined,
        approval_status: filters.approval_status || undefined,
      });
      const url = URL.createObjectURL(blob);
      const a   = document.createElement('a');
      a.href = url; a.download = 'quota_audit_log.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setError(`Export failed: ${e.message}`); }
    finally { setExporting(false); }
  };

  const sapBadge = (status) => {
    if (!status) return '—';
    if (status === 'SYNCED')              return <span className="sf-badge sf-badge--green">✓ Synced</span>;
    if (status === 'FAILED')              return <span className="sf-badge sf-badge--red">✗ Failed</span>;
    if (status === 'PENDING')             return <span className="sf-badge sf-badge--amber">⏳ Pending</span>;
    if (status === 'SKIPPED_NO_SAP_CLIENT') return <span className="sf-badge sf-badge--gray">Skipped</span>;
    return <span className="sf-badge sf-badge--gray">{status}</span>;
  };

  return (
    <div className="sf-tab-content">
      <div className="sf-forecast-tbl-bar">
        <div className="sf-filters" style={{ flex:1 }}>
          <select className="sf-select sf-select--sm" value={filters.entity_type}
            onChange={e => setFilters(p => ({ ...p, entity_type: e.target.value }))}>
            <option value="">All Types</option>
            {['REP','REGION','PRODUCT','CUSTOMER'].map(t => <option key={t}>{t}</option>)}
          </select>
          <input className="sf-input sf-input--sm" placeholder="Entity ID" value={filters.entity_id}
            onChange={e => setFilters(p => ({ ...p, entity_id: e.target.value }))} />
          <select className="sf-select sf-select--sm" value={filters.trigger_reason}
            onChange={e => setFilters(p => ({ ...p, trigger_reason: e.target.value }))}>
            <option value="">All Triggers</option>
            {['FORECAST_DELTA','PIPELINE_COVERAGE','DEMAND_SPIKE','SUPPLY_CONSTRAINT','MANUAL'].map(t =>
              <option key={t}>{t}</option>
            )}
          </select>
          <select className="sf-select sf-select--sm" value={filters.approval_status}
            onChange={e => setFilters(p => ({ ...p, approval_status: e.target.value }))}>
            <option value="">All Status</option>
            {['APPROVED','PENDING','REJECTED'].map(s => <option key={s}>{s}</option>)}
          </select>
          <button className="sf-btn sf-btn--primary sf-btn--sm" onClick={() => fetchLogs(filters)}>
            <FiSearch size={13} /> Search
          </button>
          <button className="sf-btn sf-btn--ghost sf-btn--sm" onClick={() => fetchLogs(filters)}>
            <FiRefreshCw size={13} />
          </button>
        </div>
        <button className="sf-btn sf-btn--ghost sf-btn--sm" onClick={exportCSV} disabled={exporting}>
          {exporting ? <Spinner /> : <FiDownload size={13} />} Export CSV
        </button>
      </div>

      {error && <div className="sf-error">{error}</div>}

      {loading ? (
        <div className="sf-loading"><Spinner dark /> Loading audit log…</div>
      ) : (
        <>
          <div className="sf-results-meta">{total} record{total !== 1 ? 's' : ''}</div>
          <div className="sf-table-wrap">
            <table className="sf-table">
              <thead>
                <tr>
                  <th>Log ID</th><th>Entity</th><th>Type</th><th>Old → New Quota</th>
                  <th>Delta %</th><th>Trigger</th><th>LLM Reasoning</th>
                  <th>Status</th><th>Approved By</th><th>SAP</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(r => {
                  const delta = Number(r.delta_pct || 0);
                  const reasoning = r.llm_reasoning || r.rationale || '';
                  return (
                    <tr key={r.log_id}>
                      <td style={{ fontFamily:'monospace', fontSize:'.75rem', color:'#2563eb' }}>
                        {(r.log_id || '').slice(0, 16)}
                      </td>
                      <td>
                        <span className={`sf-dim-badge ${DIM_BADGE_CLASS[r.entity_type] || 'sf-dim-default'}`}>
                          {r.entity_type}
                        </span>
                        {' '}
                        <span style={{ fontWeight:600, fontSize:'.82rem' }}>{r.entity_name || r.entity_id}</span>
                        {r.entity_name && (
                          <div style={{ fontFamily:'monospace', fontSize:'.72rem', color:'#94a3b8' }}>{r.entity_id}</div>
                        )}
                      </td>
                      <td>
                        <span className={`sf-badge sf-badge--${r.adjustment_type === 'INCREASE' ? 'green' : 'red'}`}>
                          {r.adjustment_type}
                        </span>
                      </td>
                      <td style={{ fontSize:'.82rem', whiteSpace:'nowrap' }}>
                        {fmtM(r.old_quota_amount)} → {fmtM(r.new_quota_amount)}
                      </td>
                      <td style={{ fontWeight:700, color: delta < 0 ? '#dc2626' : '#16a34a' }}>
                        {delta > 0 ? '+' : ''}{delta.toFixed(1)}%
                      </td>
                      <td>
                        <span className="sf-kanban-trigger">{r.trigger_reason}</span>
                      </td>
                      <td style={{ fontSize:'.78rem', color:'#64748b', maxWidth:220 }} title={reasoning}>
                        {reasoning.length > 60 ? reasoning.slice(0, 57) + '…' : reasoning || '—'}
                      </td>
                      <td><StatusBadge status={r.approval_status} /></td>
                      <td style={{ fontSize:'.8rem', color:'#64748b' }}>{r.approved_by || '—'}</td>
                      <td>{sapBadge(r.sap_writeback_status)}</td>
                    </tr>
                  );
                })}
                {logs.length === 0 && (
                  <tr><td colSpan={10} className="sf-empty-cell">No audit records found. Use filters and click Search.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
      <div style={{ fontSize:'.76rem', color:'#94a3b8', marginTop:12, padding:'0 4px' }}>
        This log is immutable. No records can be edited or deleted.
      </div>
    </div>
  );
}

// ─── Login page ───────────────────────────────────────────────────────────────

const SF_ROLES = [
  { value:'SALES_OPS_ADMIN',  label:'Sales Ops Admin'  },
  { value:'SALES_MANAGER',    label:'Sales Manager'    },
  { value:'SALES_REP',        label:'Sales Rep'        },
  { value:'FINANCE_APPROVER', label:'Finance Approver' },
];

function SFLoginPage({ onLogin }) {
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [role,     setRole]     = useState('SALES_OPS_ADMIN');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [showPw,   setShowPw]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const data = await api.sfLogin({ email: email.trim(), password, role });
      api.setTokens({ access_token: data.access_token, refresh_token: data.refresh_token });
      onLogin(data.user);
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sf-app" style={{ display:'flex', alignItems:'center', justifyContent:'center' }}>
      <Particles />
      <div className="sf-bg-orbs">
        <div className="sf-bg-orb sf-bg-orb-1" />
        <div className="sf-bg-orb sf-bg-orb-2" />
        <div className="sf-bg-orb sf-bg-orb-3" />
      </div>

      <div style={{
        position:'relative', zIndex:10,
        background:'rgba(255,255,255,.92)', backdropFilter:'blur(18px)',
        borderRadius:20, boxShadow:'0 24px 64px rgba(12,28,52,.18)',
        padding:'40px 36px', width:'100%', maxWidth:420,
        border:'1px solid rgba(216,227,240,.8)',
      }}>
        {/* Logo / title */}
        <div style={{ textAlign:'center', marginBottom:32 }}>
          <div style={{
            display:'inline-flex', alignItems:'center', justifyContent:'center',
            width:52, height:52, borderRadius:14, marginBottom:14,
            background:'linear-gradient(135deg,#1e3a5f,#2563eb)',
            boxShadow:'0 8px 24px rgba(37,99,235,.35)',
          }}>
            <FiTrendingUp size={24} color="#fff" />
          </div>
          <h1 style={{
            fontFamily:"'Space Grotesk',sans-serif", fontSize:'1.4rem',
            fontWeight:800, color:'#11355a', margin:'0 0 4px',
          }}>Sales Forecasting</h1>
          <p style={{ fontSize:'.82rem', color:'#64748b', margin:0 }}>
            Sign in to access your forecasting workspace
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ display:'flex', flexDirection:'column', gap:14 }}>
          <div>
            <label style={{ fontSize:'.72rem', fontWeight:700, color:'#64748b',
              textTransform:'uppercase', letterSpacing:'.06em', display:'block', marginBottom:5 }}>
              Email
            </label>
            <input
              className="sf-input"
              type="email"
              placeholder="you@savictech.com"
              value={email}
              required
              autoComplete="email"
              onChange={e => setEmail(e.target.value)}
              style={{ width:'100%' }}
            />
          </div>

          <div>
            <label style={{ fontSize:'.72rem', fontWeight:700, color:'#64748b',
              textTransform:'uppercase', letterSpacing:'.06em', display:'block', marginBottom:5 }}>
              Password
            </label>
            <div style={{ position:'relative' }}>
              <input
                className="sf-input"
                type={showPw ? 'text' : 'password'}
                placeholder="••••••••"
                value={password}
                required
                autoComplete="current-password"
                onChange={e => setPassword(e.target.value)}
                style={{ width:'100%', paddingRight:40 }}
              />
              <button type="button" onClick={() => setShowPw(v => !v)}
                style={{
                  position:'absolute', right:10, top:'50%', transform:'translateY(-50%)',
                  background:'none', border:'none', cursor:'pointer',
                  color:'#94a3b8', fontSize:13, padding:0,
                }}>
                {showPw ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <div>
            <label style={{ fontSize:'.72rem', fontWeight:700, color:'#64748b',
              textTransform:'uppercase', letterSpacing:'.06em', display:'block', marginBottom:5 }}>
              Role
            </label>
            <select
              className="sf-select"
              value={role}
              onChange={e => setRole(e.target.value)}
              style={{ width:'100%' }}
            >
              {SF_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>

          {error && (
            <div className="sf-error" style={{ marginBottom:0 }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            className="sf-btn sf-btn--primary"
            disabled={loading}
            style={{ width:'100%', justifyContent:'center', padding:'11px 0', fontSize:'.9rem', marginTop:4 }}>
            {loading ? <><Spinner /> Signing in…</> : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Main app ─────────────────────────────────────────────────────────────────

const TABS = [
  { key:'dashboard',   label:'Dashboard',   icon:<FiGrid size={14} />        },
  { key:'forecasts',   label:'Forecasts',   icon:<FiTrendingUp size={14} />  },
  { key:'quotas',      label:'Quotas',      icon:<FiTarget size={14} />      },
  { key:'adjustments', label:'Adjustments', icon:<FiSliders size={14} />     },
  { key:'workflows',   label:'Workflows',   icon:<FiCheckCircle size={14} /> },
  { key:'performance', label:'Performance', icon:<FiActivity size={14} />    },
  { key:'auditlog',    label:'Audit Log',   icon:<FiFileText size={14} />    },
];

export default function SalesForecastingApp() {
  const navigate = useNavigate();
  const [activeTab,   setActiveTab]   = useState('dashboard');
  const [panelKey,    setPanelKey]    = useState(0);
  const [user,        setUser]        = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [loggingOut,       setLoggingOut]       = useState(false);
  const [showBackConfirm,  setShowBackConfirm]  = useState(false);

  // Register session-expired callback so any 401 → back to login
  useEffect(() => {
    api.onSessionExpired(() => { setUser(null); setAuthChecked(true); });
  }, []);

  // On mount: verify stored token via /auth/me
  useEffect(() => {
    const token = api.getToken();
    if (!token) { setAuthChecked(true); return; }
    api.sfGetMe()
      .then(me => { setUser(me); setAuthChecked(true); })
      .catch(() => { api.clearTokens(); setAuthChecked(true); });
  }, []);

  const handleLogin = (userInfo) => {
    setUser(userInfo);
    setAuthChecked(true);
    setActiveTab('dashboard');
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    try { await api.sfLogout(); } catch (_) {}
    api.clearTokens();
    setUser(null);
    setLoggingOut(false);
  };

  const handleLogoutAndBack = async () => {
    setLoggingOut(true);
    try { await api.sfLogout(); } catch (_) {}
    api.clearTokens();
    setLoggingOut(false);
    navigate('/home');
  };

  const switchTab = (key) => { setActiveTab(key); setPanelKey(k => k + 1); };

  // Waiting for token check
  if (!authChecked) {
    return (
      <div className="sf-app" style={{ display:'flex', alignItems:'center', justifyContent:'center' }}>
        <Particles />
        <div style={{ position:'relative', zIndex:10, textAlign:'center' }}>
          <Spinner dark />
          <div style={{ marginTop:12, fontSize:'.84rem', color:'#64748b' }}>Checking session…</div>
        </div>
      </div>
    );
  }

  // Not authenticated → show login
  if (!user) return <SFLoginPage onLogin={handleLogin} />;

  // Authenticated → show the app
  return (
    <div className="sf-app">
      <Particles />
      <div className="sf-bg-orbs">
        <div className="sf-bg-orb sf-bg-orb-1" />
        <div className="sf-bg-orb sf-bg-orb-2" />
        <div className="sf-bg-orb sf-bg-orb-3" />
      </div>

      <header className="sf-topbar">
        <button className="sf-topbar-back" onClick={() => setShowBackConfirm(true)}>
          <FiArrowLeft size={14} /> Back
        </button>
        <div className="sf-topbar-title">
          <FiTrendingUp size={16} /> Sales Forecasting
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          {user.full_name && (
            <span style={{
              fontSize:'.75rem', color:'#64748b', fontWeight:600,
              background:'rgba(255,255,255,.7)', borderRadius:20,
              padding:'3px 10px', border:'1px solid #e2e8f0',
            }}>
              {user.full_name}
            </span>
          )}
          <div className="sf-live-badge">
            <span className="sf-live-dot" /> AI
          </div>
          <button className="sf-btn sf-btn--ghost sf-btn--sm" onClick={handleLogout} disabled={loggingOut}
            style={{ fontSize:'.75rem' }}>
            {loggingOut ? <Spinner /> : 'Sign Out'}
          </button>
        </div>
      </header>

      <nav className="sf-tabs">
        {TABS.map((t) => (
          <button key={t.key}
            className={`sf-tab-btn${activeTab === t.key ? ' active' : ''}`}
            onClick={() => switchTab(t.key)}>
            {t.icon} {t.label}
          </button>
        ))}
      </nav>

      <div className="sf-content">
        <div key={panelKey} className="sf-tab-panel sf-tab-panel-inner">
          {activeTab === 'dashboard'   && <DashboardTab />}
          {activeTab === 'forecasts'   && <ForecastsTab />}
          {activeTab === 'quotas'      && <QuotasTab />}
          {activeTab === 'adjustments' && <AdjustmentsTab />}
          {activeTab === 'workflows'   && <WorkflowsTab />}
          {activeTab === 'performance' && <PerformanceTab />}
          {activeTab === 'auditlog'    && <AuditLogTab />}
        </div>
      </div>

      {/* ── Floating AI chat widget ── */}
      <SFChatWidget />

      {/* ── Back confirmation drawer ── */}
      <SideDrawer
        open={showBackConfirm}
        onClose={() => setShowBackConfirm(false)}
        title="Going Back?"
        icon={<FiArrowLeft size={15} color="#fff" />}
        footer={
          <>
            <button className="sf-btn sf-btn--ghost"
              onClick={() => { setShowBackConfirm(false); navigate('/home'); }}>
              Stay Signed In
            </button>
            <button className="sf-btn sf-btn--danger" onClick={handleLogoutAndBack} disabled={loggingOut}>
              {loggingOut ? <><Spinner /> Signing out…</> : 'Logout'}
            </button>
          </>
        }
      >
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          <div style={{
            display:'flex', alignItems:'center', gap:12,
            background:'#eff6ff', border:'1px solid #bfdbfe',
            borderRadius:12, padding:'14px 16px',
          }}>
            <div style={{
              width:40, height:40, borderRadius:10, flexShrink:0,
              background:'linear-gradient(135deg,#1e3a5f,#2563eb)',
              display:'flex', alignItems:'center', justifyContent:'center',
            }}>
              <FiTrendingUp size={18} color="#fff" />
            </div>
            <div>
              <div style={{ fontWeight:700, fontSize:'.88rem', color:'#1e3a5f' }}>Sales Forecasting</div>
              <div style={{ fontSize:'.78rem', color:'#64748b', marginTop:2 }}>
                Signed in as <strong>{user?.email || user?.sub || 'you'}</strong>
              </div>
            </div>
          </div>
          <p style={{ fontSize:'.88rem', color:'#475569', margin:0, lineHeight:1.7 }}>
            Do you want to <strong>logout</strong> before going back, or stay signed in?
          </p>
        </div>
      </SideDrawer>
    </div>
  );
}
